/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import gql from 'graphql-tag';
import * as Urql from 'urql';
export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;
export const AccountErrorCode = {
  AccountNotConfirmed: 'ACCOUNT_NOT_CONFIRMED',
  ActivateOwnAccount: 'ACTIVATE_OWN_ACCOUNT',
  ActivateSuperuserAccount: 'ACTIVATE_SUPERUSER_ACCOUNT',
  ChannelInactive: 'CHANNEL_INACTIVE',
  DeactivateOwnAccount: 'DEACTIVATE_OWN_ACCOUNT',
  DeactivateSuperuserAccount: 'DEACTIVATE_SUPERUSER_ACCOUNT',
  DeleteNonStaffUser: 'DELETE_NON_STAFF_USER',
  DeleteOwnAccount: 'DELETE_OWN_ACCOUNT',
  DeleteStaffAccount: 'DELETE_STAFF_ACCOUNT',
  DeleteSuperuserAccount: 'DELETE_SUPERUSER_ACCOUNT',
  DisabledAuthenticationMethod: 'DISABLED_AUTHENTICATION_METHOD',
  DuplicatedInputItem: 'DUPLICATED_INPUT_ITEM',
  FileSizeLimitExceeded: 'FILE_SIZE_LIMIT_EXCEEDED',
  GraphqlError: 'GRAPHQL_ERROR',
  Inactive: 'INACTIVE',
  Invalid: 'INVALID',
  InvalidCredentials: 'INVALID_CREDENTIALS',
  InvalidPassword: 'INVALID_PASSWORD',
  JwtDecodeError: 'JWT_DECODE_ERROR',
  JwtInvalidCsrfToken: 'JWT_INVALID_CSRF_TOKEN',
  JwtInvalidToken: 'JWT_INVALID_TOKEN',
  JwtMissingToken: 'JWT_MISSING_TOKEN',
  JwtSignatureExpired: 'JWT_SIGNATURE_EXPIRED',
  LeftNotManageablePermission: 'LEFT_NOT_MANAGEABLE_PERMISSION',
  LoginAttemptDelayed: 'LOGIN_ATTEMPT_DELAYED',
  MissingChannelSlug: 'MISSING_CHANNEL_SLUG',
  NotFound: 'NOT_FOUND',
  OutOfScopeGroup: 'OUT_OF_SCOPE_GROUP',
  OutOfScopePermission: 'OUT_OF_SCOPE_PERMISSION',
  OutOfScopeUser: 'OUT_OF_SCOPE_USER',
  PasswordEntirelyNumeric: 'PASSWORD_ENTIRELY_NUMERIC',
  PasswordResetAlreadyRequested: 'PASSWORD_RESET_ALREADY_REQUESTED',
  PasswordTooCommon: 'PASSWORD_TOO_COMMON',
  PasswordTooShort: 'PASSWORD_TOO_SHORT',
  PasswordTooSimilar: 'PASSWORD_TOO_SIMILAR',
  Required: 'REQUIRED',
  Unique: 'UNIQUE',
  UnknownIpAddress: 'UNKNOWN_IP_ADDRESS'
} as const;

export type AccountErrorCode = typeof AccountErrorCode[keyof typeof AccountErrorCode];
/** Fields required to create a user. */
export type AccountRegisterInput = {
  /** Slug of a channel which will be used to notify users. Optional when only one channel exists. */
  channel?: string | null | undefined;
  /** The email address of the user. */
  email: string;
  /** Given name. */
  firstName?: string | null | undefined;
  /** User language code. */
  languageCode?: LanguageCodeEnum | null | undefined;
  /** Family name. */
  lastName?: string | null | undefined;
  /**
   * User public metadata. Can be read by any API client authorized to read the object it's attached to.
   *
   * Warning: never store sensitive information, including financial data such as credit card details.
   */
  metadata?: Array<MetadataInput> | null | undefined;
  /** Password. */
  password: string;
  /** Base of frontend URL that will be needed to create confirmation URL. Required when account confirmation is enabled. */
  redirectUrl?: string | null | undefined;
};

export type AddressInput = {
  /** City. */
  city?: string | null | undefined;
  /** District. */
  cityArea?: string | null | undefined;
  /** Company or organization. */
  companyName?: string | null | undefined;
  /** Country. */
  country?: CountryCode | null | undefined;
  /** State or province. */
  countryArea?: string | null | undefined;
  /** Given name. */
  firstName?: string | null | undefined;
  /** Family name. */
  lastName?: string | null | undefined;
  /**
   * Address public metadata. Can be read by any API client authorized to read the object it's attached to.
   *
   * Warning: never store sensitive information, including financial data such as credit card details.
   */
  metadata?: Array<MetadataInput> | null | undefined;
  /**
   * Phone number.
   *
   * Phone numbers are validated with Google's [libphonenumber](https://github.com/google/libphonenumber) library.
   */
  phone?: string | null | undefined;
  /** Postal code. */
  postalCode?: string | null | undefined;
  /**
   * Determine if the address should be validated. By default, Saleor accepts only address inputs matching ruleset from [Google Address Data]{https://chromium-i18n.appspot.com/ssl-address), using [i18naddress](https://github.com/mirumee/google-i18n-address) library. Some mutations may require additional permissions to use the the field. More info about permissions can be found in relevant mutation.
   *
   * Added in Saleor 3.19.
   *
   * Note: this API is currently in Feature Preview and can be subject to changes at later point.
   */
  skipValidation?: boolean | null | undefined;
  /** Address. */
  streetAddress1?: string | null | undefined;
  /** Address. */
  streetAddress2?: string | null | undefined;
};

export const AddressTypeEnum = {
  Billing: 'BILLING',
  Shipping: 'SHIPPING'
} as const;

export type AddressTypeEnum = typeof AddressTypeEnum[keyof typeof AddressTypeEnum];
export type CheckoutAddressValidationRules = {
  /** Determines if an error should be raised when the provided address doesn't match the expected format. Example: using letters for postal code when the numbers are expected. */
  checkFieldsFormat?: boolean | null | undefined;
  /** Determines if an error should be raised when the provided address doesn't have all the required fields. The list of required fields is dynamic and depends on the country code (use the `addressValidationRules` query to fetch them). Note: country code is mandatory for all addresses regardless of the rules provided in this input. */
  checkRequiredFields?: boolean | null | undefined;
  /** Determines if Saleor should apply normalization on address fields. Example: converting city field to uppercase letters. */
  enableFieldsNormalization?: boolean | null | undefined;
};

/**
 * Determine a current authorize status for checkout.
 *
 *     We treat the checkout as fully authorized when the sum of authorized and charged
 *     funds cover the checkout.total.
 *     We treat the checkout as partially authorized when the sum of authorized and charged
 *     funds covers only part of the checkout.total
 *     We treat the checkout as not authorized when the sum of authorized and charged funds
 *     is 0.
 *
 *     NONE - the funds are not authorized
 *     PARTIAL - the cover funds don't cover fully the checkout's total
 *     FULL - the cover funds covers the checkout's total
 *
 */
export const CheckoutAuthorizeStatusEnum = {
  Full: 'FULL',
  None: 'NONE',
  Partial: 'PARTIAL'
} as const;

export type CheckoutAuthorizeStatusEnum = typeof CheckoutAuthorizeStatusEnum[keyof typeof CheckoutAuthorizeStatusEnum];
/**
 * Determine the current charge status for the checkout.
 *
 *     The checkout is considered overcharged when the sum of the transactionItem's charge
 *     amounts exceeds the value of `checkout.total`.
 *     If the sum of the transactionItem's charge amounts equals
 *     `checkout.total`, we consider the checkout to be fully charged.
 *     If the sum of the transactionItem's charge amounts covers a part of the
 *     `checkout.total`, we treat the checkout as partially charged.
 *
 *
 *     NONE - the funds are not charged.
 *     PARTIAL - the funds that are charged don't cover the checkout's total
 *     FULL - the funds that are charged fully cover the checkout's total
 *     OVERCHARGED - the charged funds are bigger than checkout's total
 *
 */
export const CheckoutChargeStatusEnum = {
  Full: 'FULL',
  None: 'NONE',
  Overcharged: 'OVERCHARGED',
  Partial: 'PARTIAL'
} as const;

export type CheckoutChargeStatusEnum = typeof CheckoutChargeStatusEnum[keyof typeof CheckoutChargeStatusEnum];
export const CheckoutErrorCode = {
  BillingAddressNotSet: 'BILLING_ADDRESS_NOT_SET',
  ChannelInactive: 'CHANNEL_INACTIVE',
  CheckoutNotFullyPaid: 'CHECKOUT_NOT_FULLY_PAID',
  DeliveryMethodNotApplicable: 'DELIVERY_METHOD_NOT_APPLICABLE',
  EmailNotSet: 'EMAIL_NOT_SET',
  GiftCardNotApplicable: 'GIFT_CARD_NOT_APPLICABLE',
  GraphqlError: 'GRAPHQL_ERROR',
  InactivePayment: 'INACTIVE_PAYMENT',
  InsufficientStock: 'INSUFFICIENT_STOCK',
  Invalid: 'INVALID',
  InvalidShippingMethod: 'INVALID_SHIPPING_METHOD',
  MissingAddressData: 'MISSING_ADDRESS_DATA',
  MissingChannelSlug: 'MISSING_CHANNEL_SLUG',
  NonEditableGiftLine: 'NON_EDITABLE_GIFT_LINE',
  NonRemovableGiftLine: 'NON_REMOVABLE_GIFT_LINE',
  NotFound: 'NOT_FOUND',
  NoLines: 'NO_LINES',
  PaymentError: 'PAYMENT_ERROR',
  PriceOverrideReasonWithoutOverride: 'PRICE_OVERRIDE_REASON_WITHOUT_OVERRIDE',
  ProductNotPublished: 'PRODUCT_NOT_PUBLISHED',
  ProductUnavailableForPurchase: 'PRODUCT_UNAVAILABLE_FOR_PURCHASE',
  QuantityGreaterThanLimit: 'QUANTITY_GREATER_THAN_LIMIT',
  Required: 'REQUIRED',
  ShippingAddressNotSet: 'SHIPPING_ADDRESS_NOT_SET',
  ShippingChangeForbidden: 'SHIPPING_CHANGE_FORBIDDEN',
  ShippingMethodNotApplicable: 'SHIPPING_METHOD_NOT_APPLICABLE',
  ShippingMethodNotSet: 'SHIPPING_METHOD_NOT_SET',
  ShippingNotRequired: 'SHIPPING_NOT_REQUIRED',
  TaxError: 'TAX_ERROR',
  UnavailableVariantInChannel: 'UNAVAILABLE_VARIANT_IN_CHANNEL',
  Unique: 'UNIQUE',
  VoucherNotApplicable: 'VOUCHER_NOT_APPLICABLE',
  ZeroQuantity: 'ZERO_QUANTITY'
} as const;

export type CheckoutErrorCode = typeof CheckoutErrorCode[keyof typeof CheckoutErrorCode];
export type CheckoutLineUpdateInput = {
  /** ID of the line. */
  lineId?: string | number | null | undefined;
  /**
   * Checkout line public metadata. Will add and update keys. To delete keys use deleteMetadata mutation.
   *
   * Added in Saleor 3.21. Can be read by any API client authorized to read the object it's attached to.
   *
   * Warning: never store sensitive information, including financial data such as credit card details.
   */
  metadata?: Array<MetadataInput> | null | undefined;
  /** Custom price of the item. Can be set only by apps with `HANDLE_CHECKOUTS` permission. When the line with the same variant will be provided multiple times, the last price will be used. */
  price?: number | null | undefined;
  /**
   * Reason explaining why a custom `price` was set on the line, for debugging and auditing. Can be set only by apps with `HANDLE_CHECKOUTS` permission and only when the line has a `price` override. Setting a new `price` without a reason clears the previous reason. Blank values are stored as no reason. Limited to 255 characters; longer values are truncated.
   *
   * Added in Saleor 3.23.
   */
  priceOverrideReason?: string | null | undefined;
  /** The number of items purchased. Optional for apps, required for any other users. */
  quantity?: number | null | undefined;
  /**
   * ID of the product variant.
   *
   * DEPRECATED: this field will be removed. Use `lineId` instead.
   */
  variantId?: string | number | null | undefined;
};

/**
 * Represents country codes defined by the ISO 3166-1 alpha-2 standard.
 *
 * The `EU` value is DEPRECATED and will be removed in Saleor 3.21.
 */
export const CountryCode = {
  /** Andorra */
  Ad: 'AD',
  /** United Arab Emirates */
  Ae: 'AE',
  /** Afghanistan */
  Af: 'AF',
  /** Antigua and Barbuda */
  Ag: 'AG',
  /** Anguilla */
  Ai: 'AI',
  /** Albania */
  Al: 'AL',
  /** Armenia */
  Am: 'AM',
  /** Angola */
  Ao: 'AO',
  /** Antarctica */
  Aq: 'AQ',
  /** Argentina */
  Ar: 'AR',
  /** American Samoa */
  As: 'AS',
  /** Austria */
  At: 'AT',
  /** Australia */
  Au: 'AU',
  /** Aruba */
  Aw: 'AW',
  /** Åland Islands */
  Ax: 'AX',
  /** Azerbaijan */
  Az: 'AZ',
  /** Bosnia and Herzegovina */
  Ba: 'BA',
  /** Barbados */
  Bb: 'BB',
  /** Bangladesh */
  Bd: 'BD',
  /** Belgium */
  Be: 'BE',
  /** Burkina Faso */
  Bf: 'BF',
  /** Bulgaria */
  Bg: 'BG',
  /** Bahrain */
  Bh: 'BH',
  /** Burundi */
  Bi: 'BI',
  /** Benin */
  Bj: 'BJ',
  /** Saint Barthélemy */
  Bl: 'BL',
  /** Bermuda */
  Bm: 'BM',
  /** Brunei */
  Bn: 'BN',
  /** Bolivia */
  Bo: 'BO',
  /** Bonaire, Sint Eustatius and Saba */
  Bq: 'BQ',
  /** Brazil */
  Br: 'BR',
  /** Bahamas */
  Bs: 'BS',
  /** Bhutan */
  Bt: 'BT',
  /** Bouvet Island */
  Bv: 'BV',
  /** Botswana */
  Bw: 'BW',
  /** Belarus */
  By: 'BY',
  /** Belize */
  Bz: 'BZ',
  /** Canada */
  Ca: 'CA',
  /** Cocos (Keeling) Islands */
  Cc: 'CC',
  /** Congo (the Democratic Republic of the) */
  Cd: 'CD',
  /** Central African Republic */
  Cf: 'CF',
  /** Congo */
  Cg: 'CG',
  /** Switzerland */
  Ch: 'CH',
  /** Côte d'Ivoire */
  Ci: 'CI',
  /** Cook Islands */
  Ck: 'CK',
  /** Chile */
  Cl: 'CL',
  /** Cameroon */
  Cm: 'CM',
  /** China */
  Cn: 'CN',
  /** Colombia */
  Co: 'CO',
  /** Costa Rica */
  Cr: 'CR',
  /** Cuba */
  Cu: 'CU',
  /** Cabo Verde */
  Cv: 'CV',
  /** Curaçao */
  Cw: 'CW',
  /** Christmas Island */
  Cx: 'CX',
  /** Cyprus */
  Cy: 'CY',
  /** Czechia */
  Cz: 'CZ',
  /** Germany */
  De: 'DE',
  /** Djibouti */
  Dj: 'DJ',
  /** Denmark */
  Dk: 'DK',
  /** Dominica */
  Dm: 'DM',
  /** Dominican Republic */
  Do: 'DO',
  /** Algeria */
  Dz: 'DZ',
  /** Ecuador */
  Ec: 'EC',
  /** Estonia */
  Ee: 'EE',
  /** Egypt */
  Eg: 'EG',
  /** Western Sahara */
  Eh: 'EH',
  /** Eritrea */
  Er: 'ER',
  /** Spain */
  Es: 'ES',
  /** Ethiopia */
  Et: 'ET',
  /** European Union */
  Eu: 'EU',
  /** Finland */
  Fi: 'FI',
  /** Fiji */
  Fj: 'FJ',
  /** Falkland Islands (Malvinas) */
  Fk: 'FK',
  /** Micronesia */
  Fm: 'FM',
  /** Faroe Islands */
  Fo: 'FO',
  /** France */
  Fr: 'FR',
  /** Gabon */
  Ga: 'GA',
  /** United Kingdom */
  Gb: 'GB',
  /** Grenada */
  Gd: 'GD',
  /** Georgia */
  Ge: 'GE',
  /** French Guiana */
  Gf: 'GF',
  /** Guernsey */
  Gg: 'GG',
  /** Ghana */
  Gh: 'GH',
  /** Gibraltar */
  Gi: 'GI',
  /** Greenland */
  Gl: 'GL',
  /** Gambia */
  Gm: 'GM',
  /** Guinea */
  Gn: 'GN',
  /** Guadeloupe */
  Gp: 'GP',
  /** Equatorial Guinea */
  Gq: 'GQ',
  /** Greece */
  Gr: 'GR',
  /** South Georgia and the South Sandwich Islands */
  Gs: 'GS',
  /** Guatemala */
  Gt: 'GT',
  /** Guam */
  Gu: 'GU',
  /** Guinea-Bissau */
  Gw: 'GW',
  /** Guyana */
  Gy: 'GY',
  /** Hong Kong */
  Hk: 'HK',
  /** Heard Island and McDonald Islands */
  Hm: 'HM',
  /** Honduras */
  Hn: 'HN',
  /** Croatia */
  Hr: 'HR',
  /** Haiti */
  Ht: 'HT',
  /** Hungary */
  Hu: 'HU',
  /** Indonesia */
  Id: 'ID',
  /** Ireland */
  Ie: 'IE',
  /** Israel */
  Il: 'IL',
  /** Isle of Man */
  Im: 'IM',
  /** India */
  In: 'IN',
  /** British Indian Ocean Territory */
  Io: 'IO',
  /** Iraq */
  Iq: 'IQ',
  /** Iran */
  Ir: 'IR',
  /** Iceland */
  Is: 'IS',
  /** Italy */
  It: 'IT',
  /** Jersey */
  Je: 'JE',
  /** Jamaica */
  Jm: 'JM',
  /** Jordan */
  Jo: 'JO',
  /** Japan */
  Jp: 'JP',
  /** Kenya */
  Ke: 'KE',
  /** Kyrgyzstan */
  Kg: 'KG',
  /** Cambodia */
  Kh: 'KH',
  /** Kiribati */
  Ki: 'KI',
  /** Comoros */
  Km: 'KM',
  /** Saint Kitts and Nevis */
  Kn: 'KN',
  /** North Korea */
  Kp: 'KP',
  /** South Korea */
  Kr: 'KR',
  /** Kuwait */
  Kw: 'KW',
  /** Cayman Islands */
  Ky: 'KY',
  /** Kazakhstan */
  Kz: 'KZ',
  /** Laos */
  La: 'LA',
  /** Lebanon */
  Lb: 'LB',
  /** Saint Lucia */
  Lc: 'LC',
  /** Liechtenstein */
  Li: 'LI',
  /** Sri Lanka */
  Lk: 'LK',
  /** Liberia */
  Lr: 'LR',
  /** Lesotho */
  Ls: 'LS',
  /** Lithuania */
  Lt: 'LT',
  /** Luxembourg */
  Lu: 'LU',
  /** Latvia */
  Lv: 'LV',
  /** Libya */
  Ly: 'LY',
  /** Morocco */
  Ma: 'MA',
  /** Monaco */
  Mc: 'MC',
  /** Moldova */
  Md: 'MD',
  /** Montenegro */
  Me: 'ME',
  /** Saint Martin (French part) */
  Mf: 'MF',
  /** Madagascar */
  Mg: 'MG',
  /** Marshall Islands */
  Mh: 'MH',
  /** North Macedonia */
  Mk: 'MK',
  /** Mali */
  Ml: 'ML',
  /** Myanmar */
  Mm: 'MM',
  /** Mongolia */
  Mn: 'MN',
  /** Macao */
  Mo: 'MO',
  /** Northern Mariana Islands */
  Mp: 'MP',
  /** Martinique */
  Mq: 'MQ',
  /** Mauritania */
  Mr: 'MR',
  /** Montserrat */
  Ms: 'MS',
  /** Malta */
  Mt: 'MT',
  /** Mauritius */
  Mu: 'MU',
  /** Maldives */
  Mv: 'MV',
  /** Malawi */
  Mw: 'MW',
  /** Mexico */
  Mx: 'MX',
  /** Malaysia */
  My: 'MY',
  /** Mozambique */
  Mz: 'MZ',
  /** Namibia */
  Na: 'NA',
  /** New Caledonia */
  Nc: 'NC',
  /** Niger */
  Ne: 'NE',
  /** Norfolk Island */
  Nf: 'NF',
  /** Nigeria */
  Ng: 'NG',
  /** Nicaragua */
  Ni: 'NI',
  /** Netherlands */
  Nl: 'NL',
  /** Norway */
  No: 'NO',
  /** Nepal */
  Np: 'NP',
  /** Nauru */
  Nr: 'NR',
  /** Niue */
  Nu: 'NU',
  /** New Zealand */
  Nz: 'NZ',
  /** Oman */
  Om: 'OM',
  /** Panama */
  Pa: 'PA',
  /** Peru */
  Pe: 'PE',
  /** French Polynesia */
  Pf: 'PF',
  /** Papua New Guinea */
  Pg: 'PG',
  /** Philippines */
  Ph: 'PH',
  /** Pakistan */
  Pk: 'PK',
  /** Poland */
  Pl: 'PL',
  /** Saint Pierre and Miquelon */
  Pm: 'PM',
  /** Pitcairn */
  Pn: 'PN',
  /** Puerto Rico */
  Pr: 'PR',
  /** Palestine, State of */
  Ps: 'PS',
  /** Portugal */
  Pt: 'PT',
  /** Palau */
  Pw: 'PW',
  /** Paraguay */
  Py: 'PY',
  /** Qatar */
  Qa: 'QA',
  /** Réunion */
  Re: 'RE',
  /** Romania */
  Ro: 'RO',
  /** Serbia */
  Rs: 'RS',
  /** Russia */
  Ru: 'RU',
  /** Rwanda */
  Rw: 'RW',
  /** Saudi Arabia */
  Sa: 'SA',
  /** Solomon Islands */
  Sb: 'SB',
  /** Seychelles */
  Sc: 'SC',
  /** Sudan */
  Sd: 'SD',
  /** Sweden */
  Se: 'SE',
  /** Singapore */
  Sg: 'SG',
  /** Saint Helena, Ascension and Tristan da Cunha */
  Sh: 'SH',
  /** Slovenia */
  Si: 'SI',
  /** Svalbard and Jan Mayen */
  Sj: 'SJ',
  /** Slovakia */
  Sk: 'SK',
  /** Sierra Leone */
  Sl: 'SL',
  /** San Marino */
  Sm: 'SM',
  /** Senegal */
  Sn: 'SN',
  /** Somalia */
  So: 'SO',
  /** Suriname */
  Sr: 'SR',
  /** South Sudan */
  Ss: 'SS',
  /** Sao Tome and Principe */
  St: 'ST',
  /** El Salvador */
  Sv: 'SV',
  /** Sint Maarten (Dutch part) */
  Sx: 'SX',
  /** Syria */
  Sy: 'SY',
  /** Eswatini */
  Sz: 'SZ',
  /** Turks and Caicos Islands */
  Tc: 'TC',
  /** Chad */
  Td: 'TD',
  /** French Southern Territories */
  Tf: 'TF',
  /** Togo */
  Tg: 'TG',
  /** Thailand */
  Th: 'TH',
  /** Tajikistan */
  Tj: 'TJ',
  /** Tokelau */
  Tk: 'TK',
  /** Timor-Leste */
  Tl: 'TL',
  /** Turkmenistan */
  Tm: 'TM',
  /** Tunisia */
  Tn: 'TN',
  /** Tonga */
  To: 'TO',
  /** Türkiye */
  Tr: 'TR',
  /** Trinidad and Tobago */
  Tt: 'TT',
  /** Tuvalu */
  Tv: 'TV',
  /** Taiwan */
  Tw: 'TW',
  /** Tanzania */
  Tz: 'TZ',
  /** Ukraine */
  Ua: 'UA',
  /** Uganda */
  Ug: 'UG',
  /** United States Minor Outlying Islands */
  Um: 'UM',
  /** United States of America */
  Us: 'US',
  /** Uruguay */
  Uy: 'UY',
  /** Uzbekistan */
  Uz: 'UZ',
  /** Holy See */
  Va: 'VA',
  /** Saint Vincent and the Grenadines */
  Vc: 'VC',
  /** Venezuela */
  Ve: 'VE',
  /** Virgin Islands (British) */
  Vg: 'VG',
  /** Virgin Islands (U.S.) */
  Vi: 'VI',
  /** Vietnam */
  Vn: 'VN',
  /** Vanuatu */
  Vu: 'VU',
  /** Wallis and Futuna */
  Wf: 'WF',
  /** Samoa */
  Ws: 'WS',
  /** Kosovo */
  Xk: 'XK',
  /** Yemen */
  Ye: 'YE',
  /** Mayotte */
  Yt: 'YT',
  /** South Africa */
  Za: 'ZA',
  /** Zambia */
  Zm: 'ZM',
  /** Zimbabwe */
  Zw: 'ZW'
} as const;

export type CountryCode = typeof CountryCode[keyof typeof CountryCode];
export const DeliveryOptionsCalculateErrorCode = {
  GraphqlError: 'GRAPHQL_ERROR',
  Invalid: 'INVALID',
  NotFound: 'NOT_FOUND'
} as const;

export type DeliveryOptionsCalculateErrorCode = typeof DeliveryOptionsCalculateErrorCode[keyof typeof DeliveryOptionsCalculateErrorCode];
/** Language code enum. It contains all the languages supported by Saleor. */
export const LanguageCodeEnum = {
  /** Afrikaans */
  Af: 'AF',
  /** Afrikaans (Namibia) */
  AfNa: 'AF_NA',
  /** Afrikaans (South Africa) */
  AfZa: 'AF_ZA',
  /** Aghem */
  Agq: 'AGQ',
  /** Aghem (Cameroon) */
  AgqCm: 'AGQ_CM',
  /** Akan */
  Ak: 'AK',
  /** Akan (Ghana) */
  AkGh: 'AK_GH',
  /** Amharic */
  Am: 'AM',
  /** Amharic (Ethiopia) */
  AmEt: 'AM_ET',
  /** Arabic */
  Ar: 'AR',
  /** Arabic (United Arab Emirates) */
  ArAe: 'AR_AE',
  /** Arabic (Bahrain) */
  ArBh: 'AR_BH',
  /** Arabic (Djibouti) */
  ArDj: 'AR_DJ',
  /** Arabic (Algeria) */
  ArDz: 'AR_DZ',
  /** Arabic (Egypt) */
  ArEg: 'AR_EG',
  /** Arabic (Western Sahara) */
  ArEh: 'AR_EH',
  /** Arabic (Eritrea) */
  ArEr: 'AR_ER',
  /** Arabic (Israel) */
  ArIl: 'AR_IL',
  /** Arabic (Iraq) */
  ArIq: 'AR_IQ',
  /** Arabic (Jordan) */
  ArJo: 'AR_JO',
  /** Arabic (Comoros) */
  ArKm: 'AR_KM',
  /** Arabic (Kuwait) */
  ArKw: 'AR_KW',
  /** Arabic (Lebanon) */
  ArLb: 'AR_LB',
  /** Arabic (Libya) */
  ArLy: 'AR_LY',
  /** Arabic (Morocco) */
  ArMa: 'AR_MA',
  /** Arabic (Mauritania) */
  ArMr: 'AR_MR',
  /** Arabic (Oman) */
  ArOm: 'AR_OM',
  /** Arabic (Palestinian Territories) */
  ArPs: 'AR_PS',
  /** Arabic (Qatar) */
  ArQa: 'AR_QA',
  /** Arabic (Saudi Arabia) */
  ArSa: 'AR_SA',
  /** Arabic (Sudan) */
  ArSd: 'AR_SD',
  /** Arabic (Somalia) */
  ArSo: 'AR_SO',
  /** Arabic (South Sudan) */
  ArSs: 'AR_SS',
  /** Arabic (Syria) */
  ArSy: 'AR_SY',
  /** Arabic (Chad) */
  ArTd: 'AR_TD',
  /** Arabic (Tunisia) */
  ArTn: 'AR_TN',
  /** Arabic (Yemen) */
  ArYe: 'AR_YE',
  /** Assamese */
  As: 'AS',
  /** Asu */
  Asa: 'ASA',
  /** Asu (Tanzania) */
  AsaTz: 'ASA_TZ',
  /** Asturian */
  Ast: 'AST',
  /** Asturian (Spain) */
  AstEs: 'AST_ES',
  /** Assamese (India) */
  AsIn: 'AS_IN',
  /** Azerbaijani */
  Az: 'AZ',
  /** Azerbaijani (Cyrillic) */
  AzCyrl: 'AZ_CYRL',
  /** Azerbaijani (Cyrillic, Azerbaijan) */
  AzCyrlAz: 'AZ_CYRL_AZ',
  /** Azerbaijani (Latin) */
  AzLatn: 'AZ_LATN',
  /** Azerbaijani (Latin, Azerbaijan) */
  AzLatnAz: 'AZ_LATN_AZ',
  /** Basaa */
  Bas: 'BAS',
  /** Basaa (Cameroon) */
  BasCm: 'BAS_CM',
  /** Belarusian */
  Be: 'BE',
  /** Bemba */
  Bem: 'BEM',
  /** Bemba (Zambia) */
  BemZm: 'BEM_ZM',
  /** Bena */
  Bez: 'BEZ',
  /** Bena (Tanzania) */
  BezTz: 'BEZ_TZ',
  /** Belarusian (Belarus) */
  BeBy: 'BE_BY',
  /** Bulgarian */
  Bg: 'BG',
  /** Bulgarian (Bulgaria) */
  BgBg: 'BG_BG',
  /** Bambara */
  Bm: 'BM',
  /** Bambara (Mali) */
  BmMl: 'BM_ML',
  /** Bangla */
  Bn: 'BN',
  /** Bangla (Bangladesh) */
  BnBd: 'BN_BD',
  /** Bangla (India) */
  BnIn: 'BN_IN',
  /** Tibetan */
  Bo: 'BO',
  /** Tibetan (China) */
  BoCn: 'BO_CN',
  /** Tibetan (India) */
  BoIn: 'BO_IN',
  /** Breton */
  Br: 'BR',
  /** Bodo */
  Brx: 'BRX',
  /** Bodo (India) */
  BrxIn: 'BRX_IN',
  /** Breton (France) */
  BrFr: 'BR_FR',
  /** Bosnian */
  Bs: 'BS',
  /** Bosnian (Cyrillic) */
  BsCyrl: 'BS_CYRL',
  /** Bosnian (Cyrillic, Bosnia & Herzegovina) */
  BsCyrlBa: 'BS_CYRL_BA',
  /** Bosnian (Latin) */
  BsLatn: 'BS_LATN',
  /** Bosnian (Latin, Bosnia & Herzegovina) */
  BsLatnBa: 'BS_LATN_BA',
  /** Catalan */
  Ca: 'CA',
  /** Catalan (Andorra) */
  CaAd: 'CA_AD',
  /** Catalan (Spain) */
  CaEs: 'CA_ES',
  /** Catalan (Spain, Valencian) */
  CaEsValencia: 'CA_ES_VALENCIA',
  /** Catalan (France) */
  CaFr: 'CA_FR',
  /** Catalan (Italy) */
  CaIt: 'CA_IT',
  /** Chakma */
  Ccp: 'CCP',
  /** Chakma (Bangladesh) */
  CcpBd: 'CCP_BD',
  /** Chakma (India) */
  CcpIn: 'CCP_IN',
  /** Chechen */
  Ce: 'CE',
  /** Cebuano */
  Ceb: 'CEB',
  /** Cebuano (Philippines) */
  CebPh: 'CEB_PH',
  /** Chechen (Russia) */
  CeRu: 'CE_RU',
  /** Chiga */
  Cgg: 'CGG',
  /** Chiga (Uganda) */
  CggUg: 'CGG_UG',
  /** Cherokee */
  Chr: 'CHR',
  /** Cherokee (United States) */
  ChrUs: 'CHR_US',
  /** Central Kurdish */
  Ckb: 'CKB',
  /** Central Kurdish (Iraq) */
  CkbIq: 'CKB_IQ',
  /** Central Kurdish (Iran) */
  CkbIr: 'CKB_IR',
  /** Czech */
  Cs: 'CS',
  /** Czech (Czechia) */
  CsCz: 'CS_CZ',
  /** Church Slavic */
  Cu: 'CU',
  /** Church Slavic (Russia) */
  CuRu: 'CU_RU',
  /** Welsh */
  Cy: 'CY',
  /** Welsh (United Kingdom) */
  CyGb: 'CY_GB',
  /** Danish */
  Da: 'DA',
  /** Taita */
  Dav: 'DAV',
  /** Taita (Kenya) */
  DavKe: 'DAV_KE',
  /** Danish (Denmark) */
  DaDk: 'DA_DK',
  /** Danish (Greenland) */
  DaGl: 'DA_GL',
  /** German */
  De: 'DE',
  /** German (Austria) */
  DeAt: 'DE_AT',
  /** German (Belgium) */
  DeBe: 'DE_BE',
  /** German (Switzerland) */
  DeCh: 'DE_CH',
  /** German (Germany) */
  DeDe: 'DE_DE',
  /** German (Italy) */
  DeIt: 'DE_IT',
  /** German (Liechtenstein) */
  DeLi: 'DE_LI',
  /** German (Luxembourg) */
  DeLu: 'DE_LU',
  /** Zarma */
  Dje: 'DJE',
  /** Zarma (Niger) */
  DjeNe: 'DJE_NE',
  /** Lower Sorbian */
  Dsb: 'DSB',
  /** Lower Sorbian (Germany) */
  DsbDe: 'DSB_DE',
  /** Duala */
  Dua: 'DUA',
  /** Duala (Cameroon) */
  DuaCm: 'DUA_CM',
  /** Jola-Fonyi */
  Dyo: 'DYO',
  /** Jola-Fonyi (Senegal) */
  DyoSn: 'DYO_SN',
  /** Dzongkha */
  Dz: 'DZ',
  /** Dzongkha (Bhutan) */
  DzBt: 'DZ_BT',
  /** Embu */
  Ebu: 'EBU',
  /** Embu (Kenya) */
  EbuKe: 'EBU_KE',
  /** Ewe */
  Ee: 'EE',
  /** Ewe (Ghana) */
  EeGh: 'EE_GH',
  /** Ewe (Togo) */
  EeTg: 'EE_TG',
  /** Greek */
  El: 'EL',
  /** Greek (Cyprus) */
  ElCy: 'EL_CY',
  /** Greek (Greece) */
  ElGr: 'EL_GR',
  /** English */
  En: 'EN',
  /** English (United Arab Emirates) */
  EnAe: 'EN_AE',
  /** English (Antigua & Barbuda) */
  EnAg: 'EN_AG',
  /** English (Anguilla) */
  EnAi: 'EN_AI',
  /** English (American Samoa) */
  EnAs: 'EN_AS',
  /** English (Austria) */
  EnAt: 'EN_AT',
  /** English (Australia) */
  EnAu: 'EN_AU',
  /** English (Barbados) */
  EnBb: 'EN_BB',
  /** English (Belgium) */
  EnBe: 'EN_BE',
  /** English (Burundi) */
  EnBi: 'EN_BI',
  /** English (Bermuda) */
  EnBm: 'EN_BM',
  /** English (Bahamas) */
  EnBs: 'EN_BS',
  /** English (Botswana) */
  EnBw: 'EN_BW',
  /** English (Belize) */
  EnBz: 'EN_BZ',
  /** English (Canada) */
  EnCa: 'EN_CA',
  /** English (Cocos (Keeling) Islands) */
  EnCc: 'EN_CC',
  /** English (Switzerland) */
  EnCh: 'EN_CH',
  /** English (Cook Islands) */
  EnCk: 'EN_CK',
  /** English (Cameroon) */
  EnCm: 'EN_CM',
  /** English (Christmas Island) */
  EnCx: 'EN_CX',
  /** English (Cyprus) */
  EnCy: 'EN_CY',
  /** English (Germany) */
  EnDe: 'EN_DE',
  /** English (Diego Garcia) */
  EnDg: 'EN_DG',
  /** English (Denmark) */
  EnDk: 'EN_DK',
  /** English (Dominica) */
  EnDm: 'EN_DM',
  /** English (Eritrea) */
  EnEr: 'EN_ER',
  /** English (Finland) */
  EnFi: 'EN_FI',
  /** English (Fiji) */
  EnFj: 'EN_FJ',
  /** English (Falkland Islands) */
  EnFk: 'EN_FK',
  /** English (Micronesia) */
  EnFm: 'EN_FM',
  /** English (United Kingdom) */
  EnGb: 'EN_GB',
  /** English (Grenada) */
  EnGd: 'EN_GD',
  /** English (Guernsey) */
  EnGg: 'EN_GG',
  /** English (Ghana) */
  EnGh: 'EN_GH',
  /** English (Gibraltar) */
  EnGi: 'EN_GI',
  /** English (Gambia) */
  EnGm: 'EN_GM',
  /** English (Guam) */
  EnGu: 'EN_GU',
  /** English (Guyana) */
  EnGy: 'EN_GY',
  /** English (Hong Kong SAR China) */
  EnHk: 'EN_HK',
  /** English (Ireland) */
  EnIe: 'EN_IE',
  /** English (Israel) */
  EnIl: 'EN_IL',
  /** English (Isle of Man) */
  EnIm: 'EN_IM',
  /** English (India) */
  EnIn: 'EN_IN',
  /** English (British Indian Ocean Territory) */
  EnIo: 'EN_IO',
  /** English (Jersey) */
  EnJe: 'EN_JE',
  /** English (Jamaica) */
  EnJm: 'EN_JM',
  /** English (Kenya) */
  EnKe: 'EN_KE',
  /** English (Kiribati) */
  EnKi: 'EN_KI',
  /** English (St. Kitts & Nevis) */
  EnKn: 'EN_KN',
  /** English (Cayman Islands) */
  EnKy: 'EN_KY',
  /** English (St. Lucia) */
  EnLc: 'EN_LC',
  /** English (Liberia) */
  EnLr: 'EN_LR',
  /** English (Lesotho) */
  EnLs: 'EN_LS',
  /** English (Madagascar) */
  EnMg: 'EN_MG',
  /** English (Marshall Islands) */
  EnMh: 'EN_MH',
  /** English (Macao SAR China) */
  EnMo: 'EN_MO',
  /** English (Northern Mariana Islands) */
  EnMp: 'EN_MP',
  /** English (Montserrat) */
  EnMs: 'EN_MS',
  /** English (Malta) */
  EnMt: 'EN_MT',
  /** English (Mauritius) */
  EnMu: 'EN_MU',
  /** English (Malawi) */
  EnMw: 'EN_MW',
  /** English (Malaysia) */
  EnMy: 'EN_MY',
  /** English (Namibia) */
  EnNa: 'EN_NA',
  /** English (Norfolk Island) */
  EnNf: 'EN_NF',
  /** English (Nigeria) */
  EnNg: 'EN_NG',
  /** English (Netherlands) */
  EnNl: 'EN_NL',
  /** English (Nauru) */
  EnNr: 'EN_NR',
  /** English (Niue) */
  EnNu: 'EN_NU',
  /** English (New Zealand) */
  EnNz: 'EN_NZ',
  /** English (Papua New Guinea) */
  EnPg: 'EN_PG',
  /** English (Philippines) */
  EnPh: 'EN_PH',
  /** English (Pakistan) */
  EnPk: 'EN_PK',
  /** English (Pitcairn Islands) */
  EnPn: 'EN_PN',
  /** English (Puerto Rico) */
  EnPr: 'EN_PR',
  /** English (Palau) */
  EnPw: 'EN_PW',
  /** English (Rwanda) */
  EnRw: 'EN_RW',
  /** English (Solomon Islands) */
  EnSb: 'EN_SB',
  /** English (Seychelles) */
  EnSc: 'EN_SC',
  /** English (Sudan) */
  EnSd: 'EN_SD',
  /** English (Sweden) */
  EnSe: 'EN_SE',
  /** English (Singapore) */
  EnSg: 'EN_SG',
  /** English (St. Helena) */
  EnSh: 'EN_SH',
  /** English (Slovenia) */
  EnSi: 'EN_SI',
  /** English (Sierra Leone) */
  EnSl: 'EN_SL',
  /** English (South Sudan) */
  EnSs: 'EN_SS',
  /** English (Sint Maarten) */
  EnSx: 'EN_SX',
  /** English (Eswatini) */
  EnSz: 'EN_SZ',
  /** English (Turks & Caicos Islands) */
  EnTc: 'EN_TC',
  /** English (Tokelau) */
  EnTk: 'EN_TK',
  /** English (Tonga) */
  EnTo: 'EN_TO',
  /** English (Trinidad & Tobago) */
  EnTt: 'EN_TT',
  /** English (Tuvalu) */
  EnTv: 'EN_TV',
  /** English (Tanzania) */
  EnTz: 'EN_TZ',
  /** English (Uganda) */
  EnUg: 'EN_UG',
  /** English (U.S. Outlying Islands) */
  EnUm: 'EN_UM',
  /** English (United States) */
  EnUs: 'EN_US',
  /** English (St. Vincent & Grenadines) */
  EnVc: 'EN_VC',
  /** English (British Virgin Islands) */
  EnVg: 'EN_VG',
  /** English (U.S. Virgin Islands) */
  EnVi: 'EN_VI',
  /** English (Vanuatu) */
  EnVu: 'EN_VU',
  /** English (Samoa) */
  EnWs: 'EN_WS',
  /** English (South Africa) */
  EnZa: 'EN_ZA',
  /** English (Zambia) */
  EnZm: 'EN_ZM',
  /** English (Zimbabwe) */
  EnZw: 'EN_ZW',
  /** Esperanto */
  Eo: 'EO',
  /** Spanish */
  Es: 'ES',
  /** Spanish (Argentina) */
  EsAr: 'ES_AR',
  /** Spanish (Bolivia) */
  EsBo: 'ES_BO',
  /** Spanish (Brazil) */
  EsBr: 'ES_BR',
  /** Spanish (Belize) */
  EsBz: 'ES_BZ',
  /** Spanish (Chile) */
  EsCl: 'ES_CL',
  /** Spanish (Colombia) */
  EsCo: 'ES_CO',
  /** Spanish (Costa Rica) */
  EsCr: 'ES_CR',
  /** Spanish (Cuba) */
  EsCu: 'ES_CU',
  /** Spanish (Dominican Republic) */
  EsDo: 'ES_DO',
  /** Spanish (Ceuta & Melilla) */
  EsEa: 'ES_EA',
  /** Spanish (Ecuador) */
  EsEc: 'ES_EC',
  /** Spanish (Spain) */
  EsEs: 'ES_ES',
  /** Spanish (Equatorial Guinea) */
  EsGq: 'ES_GQ',
  /** Spanish (Guatemala) */
  EsGt: 'ES_GT',
  /** Spanish (Honduras) */
  EsHn: 'ES_HN',
  /** Spanish (Canary Islands) */
  EsIc: 'ES_IC',
  /** Spanish (Mexico) */
  EsMx: 'ES_MX',
  /** Spanish (Nicaragua) */
  EsNi: 'ES_NI',
  /** Spanish (Panama) */
  EsPa: 'ES_PA',
  /** Spanish (Peru) */
  EsPe: 'ES_PE',
  /** Spanish (Philippines) */
  EsPh: 'ES_PH',
  /** Spanish (Puerto Rico) */
  EsPr: 'ES_PR',
  /** Spanish (Paraguay) */
  EsPy: 'ES_PY',
  /** Spanish (El Salvador) */
  EsSv: 'ES_SV',
  /** Spanish (United States) */
  EsUs: 'ES_US',
  /** Spanish (Uruguay) */
  EsUy: 'ES_UY',
  /** Spanish (Venezuela) */
  EsVe: 'ES_VE',
  /** Estonian */
  Et: 'ET',
  /** Estonian (Estonia) */
  EtEe: 'ET_EE',
  /** Basque */
  Eu: 'EU',
  /** Basque (Spain) */
  EuEs: 'EU_ES',
  /** Ewondo */
  Ewo: 'EWO',
  /** Ewondo (Cameroon) */
  EwoCm: 'EWO_CM',
  /** Persian */
  Fa: 'FA',
  /** Persian (Afghanistan) */
  FaAf: 'FA_AF',
  /** Persian (Iran) */
  FaIr: 'FA_IR',
  /** Fulah */
  Ff: 'FF',
  /** Fulah (Adlam) */
  FfAdlm: 'FF_ADLM',
  /** Fulah (Adlam, Burkina Faso) */
  FfAdlmBf: 'FF_ADLM_BF',
  /** Fulah (Adlam, Cameroon) */
  FfAdlmCm: 'FF_ADLM_CM',
  /** Fulah (Adlam, Ghana) */
  FfAdlmGh: 'FF_ADLM_GH',
  /** Fulah (Adlam, Gambia) */
  FfAdlmGm: 'FF_ADLM_GM',
  /** Fulah (Adlam, Guinea) */
  FfAdlmGn: 'FF_ADLM_GN',
  /** Fulah (Adlam, Guinea-Bissau) */
  FfAdlmGw: 'FF_ADLM_GW',
  /** Fulah (Adlam, Liberia) */
  FfAdlmLr: 'FF_ADLM_LR',
  /** Fulah (Adlam, Mauritania) */
  FfAdlmMr: 'FF_ADLM_MR',
  /** Fulah (Adlam, Niger) */
  FfAdlmNe: 'FF_ADLM_NE',
  /** Fulah (Adlam, Nigeria) */
  FfAdlmNg: 'FF_ADLM_NG',
  /** Fulah (Adlam, Sierra Leone) */
  FfAdlmSl: 'FF_ADLM_SL',
  /** Fulah (Adlam, Senegal) */
  FfAdlmSn: 'FF_ADLM_SN',
  /** Fulah (Latin) */
  FfLatn: 'FF_LATN',
  /** Fulah (Latin, Burkina Faso) */
  FfLatnBf: 'FF_LATN_BF',
  /** Fulah (Latin, Cameroon) */
  FfLatnCm: 'FF_LATN_CM',
  /** Fulah (Latin, Ghana) */
  FfLatnGh: 'FF_LATN_GH',
  /** Fulah (Latin, Gambia) */
  FfLatnGm: 'FF_LATN_GM',
  /** Fulah (Latin, Guinea) */
  FfLatnGn: 'FF_LATN_GN',
  /** Fulah (Latin, Guinea-Bissau) */
  FfLatnGw: 'FF_LATN_GW',
  /** Fulah (Latin, Liberia) */
  FfLatnLr: 'FF_LATN_LR',
  /** Fulah (Latin, Mauritania) */
  FfLatnMr: 'FF_LATN_MR',
  /** Fulah (Latin, Niger) */
  FfLatnNe: 'FF_LATN_NE',
  /** Fulah (Latin, Nigeria) */
  FfLatnNg: 'FF_LATN_NG',
  /** Fulah (Latin, Sierra Leone) */
  FfLatnSl: 'FF_LATN_SL',
  /** Fulah (Latin, Senegal) */
  FfLatnSn: 'FF_LATN_SN',
  /** Finnish */
  Fi: 'FI',
  /** Filipino */
  Fil: 'FIL',
  /** Filipino (Philippines) */
  FilPh: 'FIL_PH',
  /** Finnish (Finland) */
  FiFi: 'FI_FI',
  /** Faroese */
  Fo: 'FO',
  /** Faroese (Denmark) */
  FoDk: 'FO_DK',
  /** Faroese (Faroe Islands) */
  FoFo: 'FO_FO',
  /** French */
  Fr: 'FR',
  /** French (Belgium) */
  FrBe: 'FR_BE',
  /** French (Burkina Faso) */
  FrBf: 'FR_BF',
  /** French (Burundi) */
  FrBi: 'FR_BI',
  /** French (Benin) */
  FrBj: 'FR_BJ',
  /** French (St. Barthélemy) */
  FrBl: 'FR_BL',
  /** French (Canada) */
  FrCa: 'FR_CA',
  /** French (Congo - Kinshasa) */
  FrCd: 'FR_CD',
  /** French (Central African Republic) */
  FrCf: 'FR_CF',
  /** French (Congo - Brazzaville) */
  FrCg: 'FR_CG',
  /** French (Switzerland) */
  FrCh: 'FR_CH',
  /** French (Côte d’Ivoire) */
  FrCi: 'FR_CI',
  /** French (Cameroon) */
  FrCm: 'FR_CM',
  /** French (Djibouti) */
  FrDj: 'FR_DJ',
  /** French (Algeria) */
  FrDz: 'FR_DZ',
  /** French (France) */
  FrFr: 'FR_FR',
  /** French (Gabon) */
  FrGa: 'FR_GA',
  /** French (French Guiana) */
  FrGf: 'FR_GF',
  /** French (Guinea) */
  FrGn: 'FR_GN',
  /** French (Guadeloupe) */
  FrGp: 'FR_GP',
  /** French (Equatorial Guinea) */
  FrGq: 'FR_GQ',
  /** French (Haiti) */
  FrHt: 'FR_HT',
  /** French (Comoros) */
  FrKm: 'FR_KM',
  /** French (Luxembourg) */
  FrLu: 'FR_LU',
  /** French (Morocco) */
  FrMa: 'FR_MA',
  /** French (Monaco) */
  FrMc: 'FR_MC',
  /** French (St. Martin) */
  FrMf: 'FR_MF',
  /** French (Madagascar) */
  FrMg: 'FR_MG',
  /** French (Mali) */
  FrMl: 'FR_ML',
  /** French (Martinique) */
  FrMq: 'FR_MQ',
  /** French (Mauritania) */
  FrMr: 'FR_MR',
  /** French (Mauritius) */
  FrMu: 'FR_MU',
  /** French (New Caledonia) */
  FrNc: 'FR_NC',
  /** French (Niger) */
  FrNe: 'FR_NE',
  /** French (French Polynesia) */
  FrPf: 'FR_PF',
  /** French (St. Pierre & Miquelon) */
  FrPm: 'FR_PM',
  /** French (Réunion) */
  FrRe: 'FR_RE',
  /** French (Rwanda) */
  FrRw: 'FR_RW',
  /** French (Seychelles) */
  FrSc: 'FR_SC',
  /** French (Senegal) */
  FrSn: 'FR_SN',
  /** French (Syria) */
  FrSy: 'FR_SY',
  /** French (Chad) */
  FrTd: 'FR_TD',
  /** French (Togo) */
  FrTg: 'FR_TG',
  /** French (Tunisia) */
  FrTn: 'FR_TN',
  /** French (Vanuatu) */
  FrVu: 'FR_VU',
  /** French (Wallis & Futuna) */
  FrWf: 'FR_WF',
  /** French (Mayotte) */
  FrYt: 'FR_YT',
  /** Friulian */
  Fur: 'FUR',
  /** Friulian (Italy) */
  FurIt: 'FUR_IT',
  /** Western Frisian */
  Fy: 'FY',
  /** Western Frisian (Netherlands) */
  FyNl: 'FY_NL',
  /** Irish */
  Ga: 'GA',
  /** Irish (United Kingdom) */
  GaGb: 'GA_GB',
  /** Irish (Ireland) */
  GaIe: 'GA_IE',
  /** Scottish Gaelic */
  Gd: 'GD',
  /** Scottish Gaelic (United Kingdom) */
  GdGb: 'GD_GB',
  /** Galician */
  Gl: 'GL',
  /** Galician (Spain) */
  GlEs: 'GL_ES',
  /** Swiss German */
  Gsw: 'GSW',
  /** Swiss German (Switzerland) */
  GswCh: 'GSW_CH',
  /** Swiss German (France) */
  GswFr: 'GSW_FR',
  /** Swiss German (Liechtenstein) */
  GswLi: 'GSW_LI',
  /** Gujarati */
  Gu: 'GU',
  /** Gusii */
  Guz: 'GUZ',
  /** Gusii (Kenya) */
  GuzKe: 'GUZ_KE',
  /** Gujarati (India) */
  GuIn: 'GU_IN',
  /** Manx */
  Gv: 'GV',
  /** Manx (Isle of Man) */
  GvIm: 'GV_IM',
  /** Hausa */
  Ha: 'HA',
  /** Hawaiian */
  Haw: 'HAW',
  /** Hawaiian (United States) */
  HawUs: 'HAW_US',
  /** Hausa (Ghana) */
  HaGh: 'HA_GH',
  /** Hausa (Niger) */
  HaNe: 'HA_NE',
  /** Hausa (Nigeria) */
  HaNg: 'HA_NG',
  /** Hebrew */
  He: 'HE',
  /** Hebrew (Israel) */
  HeIl: 'HE_IL',
  /** Hindi */
  Hi: 'HI',
  /** Hindi (India) */
  HiIn: 'HI_IN',
  /** Croatian */
  Hr: 'HR',
  /** Croatian (Bosnia & Herzegovina) */
  HrBa: 'HR_BA',
  /** Croatian (Croatia) */
  HrHr: 'HR_HR',
  /** Upper Sorbian */
  Hsb: 'HSB',
  /** Upper Sorbian (Germany) */
  HsbDe: 'HSB_DE',
  /** Hungarian */
  Hu: 'HU',
  /** Hungarian (Hungary) */
  HuHu: 'HU_HU',
  /** Armenian */
  Hy: 'HY',
  /** Armenian (Armenia) */
  HyAm: 'HY_AM',
  /** Interlingua */
  Ia: 'IA',
  /** Indonesian */
  Id: 'ID',
  /** Indonesian (Indonesia) */
  IdId: 'ID_ID',
  /** Igbo */
  Ig: 'IG',
  /** Igbo (Nigeria) */
  IgNg: 'IG_NG',
  /** Sichuan Yi */
  Ii: 'II',
  /** Sichuan Yi (China) */
  IiCn: 'II_CN',
  /** Icelandic */
  Is: 'IS',
  /** Icelandic (Iceland) */
  IsIs: 'IS_IS',
  /** Italian */
  It: 'IT',
  /** Italian (Switzerland) */
  ItCh: 'IT_CH',
  /** Italian (Italy) */
  ItIt: 'IT_IT',
  /** Italian (San Marino) */
  ItSm: 'IT_SM',
  /** Italian (Vatican City) */
  ItVa: 'IT_VA',
  /** Japanese */
  Ja: 'JA',
  /** Japanese (Japan) */
  JaJp: 'JA_JP',
  /** Ngomba */
  Jgo: 'JGO',
  /** Ngomba (Cameroon) */
  JgoCm: 'JGO_CM',
  /** Machame */
  Jmc: 'JMC',
  /** Machame (Tanzania) */
  JmcTz: 'JMC_TZ',
  /** Javanese */
  Jv: 'JV',
  /** Javanese (Indonesia) */
  JvId: 'JV_ID',
  /** Georgian */
  Ka: 'KA',
  /** Kabyle */
  Kab: 'KAB',
  /** Kabyle (Algeria) */
  KabDz: 'KAB_DZ',
  /** Kamba */
  Kam: 'KAM',
  /** Kamba (Kenya) */
  KamKe: 'KAM_KE',
  /** Georgian (Georgia) */
  KaGe: 'KA_GE',
  /** Makonde */
  Kde: 'KDE',
  /** Makonde (Tanzania) */
  KdeTz: 'KDE_TZ',
  /** Kabuverdianu */
  Kea: 'KEA',
  /** Kabuverdianu (Cape Verde) */
  KeaCv: 'KEA_CV',
  /** Koyra Chiini */
  Khq: 'KHQ',
  /** Koyra Chiini (Mali) */
  KhqMl: 'KHQ_ML',
  /** Kikuyu */
  Ki: 'KI',
  /** Kikuyu (Kenya) */
  KiKe: 'KI_KE',
  /** Kazakh */
  Kk: 'KK',
  /** Kako */
  Kkj: 'KKJ',
  /** Kako (Cameroon) */
  KkjCm: 'KKJ_CM',
  /** Kazakh (Kazakhstan) */
  KkKz: 'KK_KZ',
  /** Kalaallisut */
  Kl: 'KL',
  /** Kalenjin */
  Kln: 'KLN',
  /** Kalenjin (Kenya) */
  KlnKe: 'KLN_KE',
  /** Kalaallisut (Greenland) */
  KlGl: 'KL_GL',
  /** Khmer */
  Km: 'KM',
  /** Khmer (Cambodia) */
  KmKh: 'KM_KH',
  /** Kannada */
  Kn: 'KN',
  /** Kannada (India) */
  KnIn: 'KN_IN',
  /** Korean */
  Ko: 'KO',
  /** Konkani */
  Kok: 'KOK',
  /** Konkani (India) */
  KokIn: 'KOK_IN',
  /** Korean (North Korea) */
  KoKp: 'KO_KP',
  /** Korean (South Korea) */
  KoKr: 'KO_KR',
  /** Kashmiri */
  Ks: 'KS',
  /** Shambala */
  Ksb: 'KSB',
  /** Shambala (Tanzania) */
  KsbTz: 'KSB_TZ',
  /** Bafia */
  Ksf: 'KSF',
  /** Bafia (Cameroon) */
  KsfCm: 'KSF_CM',
  /** Colognian */
  Ksh: 'KSH',
  /** Colognian (Germany) */
  KshDe: 'KSH_DE',
  /** Kashmiri (Arabic) */
  KsArab: 'KS_ARAB',
  /** Kashmiri (Arabic, India) */
  KsArabIn: 'KS_ARAB_IN',
  /** Kurdish */
  Ku: 'KU',
  /** Kurdish (Turkey) */
  KuTr: 'KU_TR',
  /** Cornish */
  Kw: 'KW',
  /** Cornish (United Kingdom) */
  KwGb: 'KW_GB',
  /** Kyrgyz */
  Ky: 'KY',
  /** Kyrgyz (Kyrgyzstan) */
  KyKg: 'KY_KG',
  /** Langi */
  Lag: 'LAG',
  /** Langi (Tanzania) */
  LagTz: 'LAG_TZ',
  /** Luxembourgish */
  Lb: 'LB',
  /** Luxembourgish (Luxembourg) */
  LbLu: 'LB_LU',
  /** Ganda */
  Lg: 'LG',
  /** Ganda (Uganda) */
  LgUg: 'LG_UG',
  /** Lakota */
  Lkt: 'LKT',
  /** Lakota (United States) */
  LktUs: 'LKT_US',
  /** Lingala */
  Ln: 'LN',
  /** Lingala (Angola) */
  LnAo: 'LN_AO',
  /** Lingala (Congo - Kinshasa) */
  LnCd: 'LN_CD',
  /** Lingala (Central African Republic) */
  LnCf: 'LN_CF',
  /** Lingala (Congo - Brazzaville) */
  LnCg: 'LN_CG',
  /** Lao */
  Lo: 'LO',
  /** Lao (Laos) */
  LoLa: 'LO_LA',
  /** Northern Luri */
  Lrc: 'LRC',
  /** Northern Luri (Iraq) */
  LrcIq: 'LRC_IQ',
  /** Northern Luri (Iran) */
  LrcIr: 'LRC_IR',
  /** Lithuanian */
  Lt: 'LT',
  /** Lithuanian (Lithuania) */
  LtLt: 'LT_LT',
  /** Luba-Katanga */
  Lu: 'LU',
  /** Luo */
  Luo: 'LUO',
  /** Luo (Kenya) */
  LuoKe: 'LUO_KE',
  /** Luyia */
  Luy: 'LUY',
  /** Luyia (Kenya) */
  LuyKe: 'LUY_KE',
  /** Luba-Katanga (Congo - Kinshasa) */
  LuCd: 'LU_CD',
  /** Latvian */
  Lv: 'LV',
  /** Latvian (Latvia) */
  LvLv: 'LV_LV',
  /** Maithili */
  Mai: 'MAI',
  /** Maithili (India) */
  MaiIn: 'MAI_IN',
  /** Masai */
  Mas: 'MAS',
  /** Masai (Kenya) */
  MasKe: 'MAS_KE',
  /** Masai (Tanzania) */
  MasTz: 'MAS_TZ',
  /** Meru */
  Mer: 'MER',
  /** Meru (Kenya) */
  MerKe: 'MER_KE',
  /** Morisyen */
  Mfe: 'MFE',
  /** Morisyen (Mauritius) */
  MfeMu: 'MFE_MU',
  /** Malagasy */
  Mg: 'MG',
  /** Makhuwa-Meetto */
  Mgh: 'MGH',
  /** Makhuwa-Meetto (Mozambique) */
  MghMz: 'MGH_MZ',
  /** Metaʼ */
  Mgo: 'MGO',
  /** Metaʼ (Cameroon) */
  MgoCm: 'MGO_CM',
  /** Malagasy (Madagascar) */
  MgMg: 'MG_MG',
  /** Maori */
  Mi: 'MI',
  /** Maori (New Zealand) */
  MiNz: 'MI_NZ',
  /** Macedonian */
  Mk: 'MK',
  /** Macedonian (North Macedonia) */
  MkMk: 'MK_MK',
  /** Malayalam */
  Ml: 'ML',
  /** Malayalam (India) */
  MlIn: 'ML_IN',
  /** Mongolian */
  Mn: 'MN',
  /** Manipuri */
  Mni: 'MNI',
  /** Manipuri (Bangla) */
  MniBeng: 'MNI_BENG',
  /** Manipuri (Bangla, India) */
  MniBengIn: 'MNI_BENG_IN',
  /** Mongolian (Mongolia) */
  MnMn: 'MN_MN',
  /** Marathi */
  Mr: 'MR',
  /** Marathi (India) */
  MrIn: 'MR_IN',
  /** Malay */
  Ms: 'MS',
  /** Malay (Brunei) */
  MsBn: 'MS_BN',
  /** Malay (Indonesia) */
  MsId: 'MS_ID',
  /** Malay (Malaysia) */
  MsMy: 'MS_MY',
  /** Malay (Singapore) */
  MsSg: 'MS_SG',
  /** Maltese */
  Mt: 'MT',
  /** Maltese (Malta) */
  MtMt: 'MT_MT',
  /** Mundang */
  Mua: 'MUA',
  /** Mundang (Cameroon) */
  MuaCm: 'MUA_CM',
  /** Burmese */
  My: 'MY',
  /** Burmese (Myanmar (Burma)) */
  MyMm: 'MY_MM',
  /** Mazanderani */
  Mzn: 'MZN',
  /** Mazanderani (Iran) */
  MznIr: 'MZN_IR',
  /** Nama */
  Naq: 'NAQ',
  /** Nama (Namibia) */
  NaqNa: 'NAQ_NA',
  /** Norwegian Bokmål */
  Nb: 'NB',
  /** Norwegian Bokmål (Norway) */
  NbNo: 'NB_NO',
  /** Norwegian Bokmål (Svalbard & Jan Mayen) */
  NbSj: 'NB_SJ',
  /** North Ndebele */
  Nd: 'ND',
  /** Low German */
  Nds: 'NDS',
  /** Low German (Germany) */
  NdsDe: 'NDS_DE',
  /** Low German (Netherlands) */
  NdsNl: 'NDS_NL',
  /** North Ndebele (Zimbabwe) */
  NdZw: 'ND_ZW',
  /** Nepali */
  Ne: 'NE',
  /** Nepali (India) */
  NeIn: 'NE_IN',
  /** Nepali (Nepal) */
  NeNp: 'NE_NP',
  /** Dutch */
  Nl: 'NL',
  /** Dutch (Aruba) */
  NlAw: 'NL_AW',
  /** Dutch (Belgium) */
  NlBe: 'NL_BE',
  /** Dutch (Caribbean Netherlands) */
  NlBq: 'NL_BQ',
  /** Dutch (Curaçao) */
  NlCw: 'NL_CW',
  /** Dutch (Netherlands) */
  NlNl: 'NL_NL',
  /** Dutch (Suriname) */
  NlSr: 'NL_SR',
  /** Dutch (Sint Maarten) */
  NlSx: 'NL_SX',
  /** Kwasio */
  Nmg: 'NMG',
  /** Kwasio (Cameroon) */
  NmgCm: 'NMG_CM',
  /** Norwegian Nynorsk */
  Nn: 'NN',
  /** Ngiemboon */
  Nnh: 'NNH',
  /** Ngiemboon (Cameroon) */
  NnhCm: 'NNH_CM',
  /** Norwegian Nynorsk (Norway) */
  NnNo: 'NN_NO',
  /** Nuer */
  Nus: 'NUS',
  /** Nuer (South Sudan) */
  NusSs: 'NUS_SS',
  /** Nyankole */
  Nyn: 'NYN',
  /** Nyankole (Uganda) */
  NynUg: 'NYN_UG',
  /** Oromo */
  Om: 'OM',
  /** Oromo (Ethiopia) */
  OmEt: 'OM_ET',
  /** Oromo (Kenya) */
  OmKe: 'OM_KE',
  /** Odia */
  Or: 'OR',
  /** Odia (India) */
  OrIn: 'OR_IN',
  /** Ossetic */
  Os: 'OS',
  /** Ossetic (Georgia) */
  OsGe: 'OS_GE',
  /** Ossetic (Russia) */
  OsRu: 'OS_RU',
  /** Punjabi */
  Pa: 'PA',
  /** Punjabi (Arabic) */
  PaArab: 'PA_ARAB',
  /** Punjabi (Arabic, Pakistan) */
  PaArabPk: 'PA_ARAB_PK',
  /** Punjabi (Gurmukhi) */
  PaGuru: 'PA_GURU',
  /** Punjabi (Gurmukhi, India) */
  PaGuruIn: 'PA_GURU_IN',
  /** Nigerian Pidgin */
  Pcm: 'PCM',
  /** Nigerian Pidgin (Nigeria) */
  PcmNg: 'PCM_NG',
  /** Polish */
  Pl: 'PL',
  /** Polish (Poland) */
  PlPl: 'PL_PL',
  /** Prussian */
  Prg: 'PRG',
  /** Pashto */
  Ps: 'PS',
  /** Pashto (Afghanistan) */
  PsAf: 'PS_AF',
  /** Pashto (Pakistan) */
  PsPk: 'PS_PK',
  /** Portuguese */
  Pt: 'PT',
  /** Portuguese (Angola) */
  PtAo: 'PT_AO',
  /** Portuguese (Brazil) */
  PtBr: 'PT_BR',
  /** Portuguese (Switzerland) */
  PtCh: 'PT_CH',
  /** Portuguese (Cape Verde) */
  PtCv: 'PT_CV',
  /** Portuguese (Equatorial Guinea) */
  PtGq: 'PT_GQ',
  /** Portuguese (Guinea-Bissau) */
  PtGw: 'PT_GW',
  /** Portuguese (Luxembourg) */
  PtLu: 'PT_LU',
  /** Portuguese (Macao SAR China) */
  PtMo: 'PT_MO',
  /** Portuguese (Mozambique) */
  PtMz: 'PT_MZ',
  /** Portuguese (Portugal) */
  PtPt: 'PT_PT',
  /** Portuguese (São Tomé & Príncipe) */
  PtSt: 'PT_ST',
  /** Portuguese (Timor-Leste) */
  PtTl: 'PT_TL',
  /** Quechua */
  Qu: 'QU',
  /** Quechua (Bolivia) */
  QuBo: 'QU_BO',
  /** Quechua (Ecuador) */
  QuEc: 'QU_EC',
  /** Quechua (Peru) */
  QuPe: 'QU_PE',
  /** Romansh */
  Rm: 'RM',
  /** Romansh (Switzerland) */
  RmCh: 'RM_CH',
  /** Rundi */
  Rn: 'RN',
  /** Rundi (Burundi) */
  RnBi: 'RN_BI',
  /** Romanian */
  Ro: 'RO',
  /** Rombo */
  Rof: 'ROF',
  /** Rombo (Tanzania) */
  RofTz: 'ROF_TZ',
  /** Romanian (Moldova) */
  RoMd: 'RO_MD',
  /** Romanian (Romania) */
  RoRo: 'RO_RO',
  /** Russian */
  Ru: 'RU',
  /** Russian (Belarus) */
  RuBy: 'RU_BY',
  /** Russian (Kyrgyzstan) */
  RuKg: 'RU_KG',
  /** Russian (Kazakhstan) */
  RuKz: 'RU_KZ',
  /** Russian (Moldova) */
  RuMd: 'RU_MD',
  /** Russian (Russia) */
  RuRu: 'RU_RU',
  /** Russian (Ukraine) */
  RuUa: 'RU_UA',
  /** Kinyarwanda */
  Rw: 'RW',
  /** Rwa */
  Rwk: 'RWK',
  /** Rwa (Tanzania) */
  RwkTz: 'RWK_TZ',
  /** Kinyarwanda (Rwanda) */
  RwRw: 'RW_RW',
  /** Sakha */
  Sah: 'SAH',
  /** Sakha (Russia) */
  SahRu: 'SAH_RU',
  /** Samburu */
  Saq: 'SAQ',
  /** Samburu (Kenya) */
  SaqKe: 'SAQ_KE',
  /** Santali */
  Sat: 'SAT',
  /** Santali (Ol Chiki) */
  SatOlck: 'SAT_OLCK',
  /** Santali (Ol Chiki, India) */
  SatOlckIn: 'SAT_OLCK_IN',
  /** Sangu */
  Sbp: 'SBP',
  /** Sangu (Tanzania) */
  SbpTz: 'SBP_TZ',
  /** Sindhi */
  Sd: 'SD',
  /** Sindhi (Arabic) */
  SdArab: 'SD_ARAB',
  /** Sindhi (Arabic, Pakistan) */
  SdArabPk: 'SD_ARAB_PK',
  /** Sindhi (Devanagari) */
  SdDeva: 'SD_DEVA',
  /** Sindhi (Devanagari, India) */
  SdDevaIn: 'SD_DEVA_IN',
  /** Northern Sami */
  Se: 'SE',
  /** Sena */
  Seh: 'SEH',
  /** Sena (Mozambique) */
  SehMz: 'SEH_MZ',
  /** Koyraboro Senni */
  Ses: 'SES',
  /** Koyraboro Senni (Mali) */
  SesMl: 'SES_ML',
  /** Northern Sami (Finland) */
  SeFi: 'SE_FI',
  /** Northern Sami (Norway) */
  SeNo: 'SE_NO',
  /** Northern Sami (Sweden) */
  SeSe: 'SE_SE',
  /** Sango */
  Sg: 'SG',
  /** Sango (Central African Republic) */
  SgCf: 'SG_CF',
  /** Tachelhit */
  Shi: 'SHI',
  /** Tachelhit (Latin) */
  ShiLatn: 'SHI_LATN',
  /** Tachelhit (Latin, Morocco) */
  ShiLatnMa: 'SHI_LATN_MA',
  /** Tachelhit (Tifinagh) */
  ShiTfng: 'SHI_TFNG',
  /** Tachelhit (Tifinagh, Morocco) */
  ShiTfngMa: 'SHI_TFNG_MA',
  /** Sinhala */
  Si: 'SI',
  /** Sinhala (Sri Lanka) */
  SiLk: 'SI_LK',
  /** Slovak */
  Sk: 'SK',
  /** Slovak (Slovakia) */
  SkSk: 'SK_SK',
  /** Slovenian */
  Sl: 'SL',
  /** Slovenian (Slovenia) */
  SlSi: 'SL_SI',
  /** Inari Sami */
  Smn: 'SMN',
  /** Inari Sami (Finland) */
  SmnFi: 'SMN_FI',
  /** Shona */
  Sn: 'SN',
  /** Shona (Zimbabwe) */
  SnZw: 'SN_ZW',
  /** Somali */
  So: 'SO',
  /** Somali (Djibouti) */
  SoDj: 'SO_DJ',
  /** Somali (Ethiopia) */
  SoEt: 'SO_ET',
  /** Somali (Kenya) */
  SoKe: 'SO_KE',
  /** Somali (Somalia) */
  SoSo: 'SO_SO',
  /** Albanian */
  Sq: 'SQ',
  /** Albanian (Albania) */
  SqAl: 'SQ_AL',
  /** Albanian (North Macedonia) */
  SqMk: 'SQ_MK',
  /** Albanian (Kosovo) */
  SqXk: 'SQ_XK',
  /** Serbian */
  Sr: 'SR',
  /** Serbian (Cyrillic) */
  SrCyrl: 'SR_CYRL',
  /** Serbian (Cyrillic, Bosnia & Herzegovina) */
  SrCyrlBa: 'SR_CYRL_BA',
  /** Serbian (Cyrillic, Montenegro) */
  SrCyrlMe: 'SR_CYRL_ME',
  /** Serbian (Cyrillic, Serbia) */
  SrCyrlRs: 'SR_CYRL_RS',
  /** Serbian (Cyrillic, Kosovo) */
  SrCyrlXk: 'SR_CYRL_XK',
  /** Serbian (Latin) */
  SrLatn: 'SR_LATN',
  /** Serbian (Latin, Bosnia & Herzegovina) */
  SrLatnBa: 'SR_LATN_BA',
  /** Serbian (Latin, Montenegro) */
  SrLatnMe: 'SR_LATN_ME',
  /** Serbian (Latin, Serbia) */
  SrLatnRs: 'SR_LATN_RS',
  /** Serbian (Latin, Kosovo) */
  SrLatnXk: 'SR_LATN_XK',
  /** Sundanese */
  Su: 'SU',
  /** Sundanese (Latin) */
  SuLatn: 'SU_LATN',
  /** Sundanese (Latin, Indonesia) */
  SuLatnId: 'SU_LATN_ID',
  /** Swedish */
  Sv: 'SV',
  /** Swedish (Åland Islands) */
  SvAx: 'SV_AX',
  /** Swedish (Finland) */
  SvFi: 'SV_FI',
  /** Swedish (Sweden) */
  SvSe: 'SV_SE',
  /** Swahili */
  Sw: 'SW',
  /** Swahili (Congo - Kinshasa) */
  SwCd: 'SW_CD',
  /** Swahili (Kenya) */
  SwKe: 'SW_KE',
  /** Swahili (Tanzania) */
  SwTz: 'SW_TZ',
  /** Swahili (Uganda) */
  SwUg: 'SW_UG',
  /** Tamil */
  Ta: 'TA',
  /** Tamil (India) */
  TaIn: 'TA_IN',
  /** Tamil (Sri Lanka) */
  TaLk: 'TA_LK',
  /** Tamil (Malaysia) */
  TaMy: 'TA_MY',
  /** Tamil (Singapore) */
  TaSg: 'TA_SG',
  /** Telugu */
  Te: 'TE',
  /** Teso */
  Teo: 'TEO',
  /** Teso (Kenya) */
  TeoKe: 'TEO_KE',
  /** Teso (Uganda) */
  TeoUg: 'TEO_UG',
  /** Telugu (India) */
  TeIn: 'TE_IN',
  /** Tajik */
  Tg: 'TG',
  /** Tajik (Tajikistan) */
  TgTj: 'TG_TJ',
  /** Thai */
  Th: 'TH',
  /** Thai (Thailand) */
  ThTh: 'TH_TH',
  /** Tigrinya */
  Ti: 'TI',
  /** Tigrinya (Eritrea) */
  TiEr: 'TI_ER',
  /** Tigrinya (Ethiopia) */
  TiEt: 'TI_ET',
  /** Turkmen */
  Tk: 'TK',
  /** Turkmen (Turkmenistan) */
  TkTm: 'TK_TM',
  /** Tongan */
  To: 'TO',
  /** Tongan (Tonga) */
  ToTo: 'TO_TO',
  /** Turkish */
  Tr: 'TR',
  /** Turkish (Cyprus) */
  TrCy: 'TR_CY',
  /** Turkish (Turkey) */
  TrTr: 'TR_TR',
  /** Tatar */
  Tt: 'TT',
  /** Tatar (Russia) */
  TtRu: 'TT_RU',
  /** Tasawaq */
  Twq: 'TWQ',
  /** Tasawaq (Niger) */
  TwqNe: 'TWQ_NE',
  /** Central Atlas Tamazight */
  Tzm: 'TZM',
  /** Central Atlas Tamazight (Morocco) */
  TzmMa: 'TZM_MA',
  /** Uyghur */
  Ug: 'UG',
  /** Uyghur (China) */
  UgCn: 'UG_CN',
  /** Ukrainian */
  Uk: 'UK',
  /** Ukrainian (Ukraine) */
  UkUa: 'UK_UA',
  /** Urdu */
  Ur: 'UR',
  /** Urdu (India) */
  UrIn: 'UR_IN',
  /** Urdu (Pakistan) */
  UrPk: 'UR_PK',
  /** Uzbek */
  Uz: 'UZ',
  /** Uzbek (Arabic) */
  UzArab: 'UZ_ARAB',
  /** Uzbek (Arabic, Afghanistan) */
  UzArabAf: 'UZ_ARAB_AF',
  /** Uzbek (Cyrillic) */
  UzCyrl: 'UZ_CYRL',
  /** Uzbek (Cyrillic, Uzbekistan) */
  UzCyrlUz: 'UZ_CYRL_UZ',
  /** Uzbek (Latin) */
  UzLatn: 'UZ_LATN',
  /** Uzbek (Latin, Uzbekistan) */
  UzLatnUz: 'UZ_LATN_UZ',
  /** Vai */
  Vai: 'VAI',
  /** Vai (Latin) */
  VaiLatn: 'VAI_LATN',
  /** Vai (Latin, Liberia) */
  VaiLatnLr: 'VAI_LATN_LR',
  /** Vai (Vai) */
  VaiVaii: 'VAI_VAII',
  /** Vai (Vai, Liberia) */
  VaiVaiiLr: 'VAI_VAII_LR',
  /** Vietnamese */
  Vi: 'VI',
  /** Vietnamese (Vietnam) */
  ViVn: 'VI_VN',
  /** Volapük */
  Vo: 'VO',
  /** Vunjo */
  Vun: 'VUN',
  /** Vunjo (Tanzania) */
  VunTz: 'VUN_TZ',
  /** Walser */
  Wae: 'WAE',
  /** Walser (Switzerland) */
  WaeCh: 'WAE_CH',
  /** Wolof */
  Wo: 'WO',
  /** Wolof (Senegal) */
  WoSn: 'WO_SN',
  /** Xhosa */
  Xh: 'XH',
  /** Xhosa (South Africa) */
  XhZa: 'XH_ZA',
  /** Soga */
  Xog: 'XOG',
  /** Soga (Uganda) */
  XogUg: 'XOG_UG',
  /** Yangben */
  Yav: 'YAV',
  /** Yangben (Cameroon) */
  YavCm: 'YAV_CM',
  /** Yiddish */
  Yi: 'YI',
  /** Yoruba */
  Yo: 'YO',
  /** Yoruba (Benin) */
  YoBj: 'YO_BJ',
  /** Yoruba (Nigeria) */
  YoNg: 'YO_NG',
  /** Cantonese */
  Yue: 'YUE',
  /** Cantonese (Simplified) */
  YueHans: 'YUE_HANS',
  /** Cantonese (Simplified, China) */
  YueHansCn: 'YUE_HANS_CN',
  /** Cantonese (Traditional) */
  YueHant: 'YUE_HANT',
  /** Cantonese (Traditional, Hong Kong SAR China) */
  YueHantHk: 'YUE_HANT_HK',
  /** Standard Moroccan Tamazight */
  Zgh: 'ZGH',
  /** Standard Moroccan Tamazight (Morocco) */
  ZghMa: 'ZGH_MA',
  /** Chinese */
  Zh: 'ZH',
  /** Chinese (Simplified) */
  ZhHans: 'ZH_HANS',
  /** Chinese (Simplified, China) */
  ZhHansCn: 'ZH_HANS_CN',
  /** Chinese (Simplified, Hong Kong SAR China) */
  ZhHansHk: 'ZH_HANS_HK',
  /** Chinese (Simplified, Macao SAR China) */
  ZhHansMo: 'ZH_HANS_MO',
  /** Chinese (Simplified, Singapore) */
  ZhHansSg: 'ZH_HANS_SG',
  /** Chinese (Traditional) */
  ZhHant: 'ZH_HANT',
  /** Chinese (Traditional, Hong Kong SAR China) */
  ZhHantHk: 'ZH_HANT_HK',
  /** Chinese (Traditional, Macao SAR China) */
  ZhHantMo: 'ZH_HANT_MO',
  /** Chinese (Traditional, Taiwan) */
  ZhHantTw: 'ZH_HANT_TW',
  /** Zulu */
  Zu: 'ZU',
  /** Zulu (South Africa) */
  ZuZa: 'ZU_ZA'
} as const;

export type LanguageCodeEnum = typeof LanguageCodeEnum[keyof typeof LanguageCodeEnum];
export type MetadataInput = {
  /** Key of a metadata item. */
  key: string;
  /** Value of a metadata item. */
  value: string;
};

/**
 * Determine a current authorize status for order.
 *
 *     We treat the order as fully authorized when the sum of authorized and charged funds
 *     cover the `order.total`-`order.totalGrantedRefund`.
 *     We treat the order as partially authorized when the sum of authorized and charged
 *     funds covers only part of the `order.total`-`order.totalGrantedRefund`.
 *     We treat the order as not authorized when the sum of authorized and charged funds is
 *     0.
 *
 *     NONE - the funds are not authorized
 *     PARTIAL - the funds that are authorized and charged don't cover fully the
 *     `order.total`-`order.totalGrantedRefund`
 *     FULL - the funds that are authorized and charged fully cover the
 *     `order.total`-`order.totalGrantedRefund`
 *
 */
export const OrderAuthorizeStatusEnum = {
  Full: 'FULL',
  None: 'NONE',
  Partial: 'PARTIAL'
} as const;

export type OrderAuthorizeStatusEnum = typeof OrderAuthorizeStatusEnum[keyof typeof OrderAuthorizeStatusEnum];
/**
 * Determine the current charge status for the order.
 *
 *     An order is considered overcharged when the sum of the
 *     transactionItem's charge amounts exceeds the value of
 *     `order.total` - `order.totalGrantedRefund`.
 *     If the sum of the transactionItem's charge amounts equals
 *     `order.total` - `order.totalGrantedRefund`, we consider the order to be fully
 *     charged.
 *     If the sum of the transactionItem's charge amounts covers a part of the
 *     `order.total` - `order.totalGrantedRefund`, we treat the order as partially charged.
 *
 *     NONE - the funds are not charged.
 *     PARTIAL - the funds that are charged don't cover the
 *     `order.total`-`order.totalGrantedRefund`
 *     FULL - the funds that are charged fully cover the
 *     `order.total`-`order.totalGrantedRefund`
 *     OVERCHARGED - the charged funds are bigger than the
 *     `order.total`-`order.totalGrantedRefund`
 *
 */
export const OrderChargeStatusEnum = {
  Full: 'FULL',
  None: 'NONE',
  Overcharged: 'OVERCHARGED',
  Partial: 'PARTIAL'
} as const;

export type OrderChargeStatusEnum = typeof OrderChargeStatusEnum[keyof typeof OrderChargeStatusEnum];
export const OrderDiscountType = {
  Manual: 'MANUAL',
  OrderPromotion: 'ORDER_PROMOTION',
  Promotion: 'PROMOTION',
  Sale: 'SALE',
  Voucher: 'VOUCHER'
} as const;

export type OrderDiscountType = typeof OrderDiscountType[keyof typeof OrderDiscountType];
export const PaymentGatewayConfigErrorCode = {
  GraphqlError: 'GRAPHQL_ERROR',
  Invalid: 'INVALID',
  NotFound: 'NOT_FOUND'
} as const;

export type PaymentGatewayConfigErrorCode = typeof PaymentGatewayConfigErrorCode[keyof typeof PaymentGatewayConfigErrorCode];
export const PaymentGatewayInitializeErrorCode = {
  GraphqlError: 'GRAPHQL_ERROR',
  Invalid: 'INVALID',
  NotFound: 'NOT_FOUND'
} as const;

export type PaymentGatewayInitializeErrorCode = typeof PaymentGatewayInitializeErrorCode[keyof typeof PaymentGatewayInitializeErrorCode];
export type PaymentGatewayToInitialize = {
  /** The data that will be passed to the payment gateway. */
  data?: any;
  /** The identifier of the payment gateway app to initialize. */
  id: string;
};

export const ProductMediaType = {
  Image: 'IMAGE',
  Video: 'VIDEO'
} as const;

export type ProductMediaType = typeof ProductMediaType[keyof typeof ProductMediaType];
/** The specific reason why a promo code cannot be applied. A code that has not been proven usable yet is only ever reported as `NOT_FOUND`, `EXPIRED` or `USAGE_LIMIT_REACHED`, so that a rejected code cannot be told apart from one that does not exist. The reason never depends on the caller's permissions. */
export const PromoCodeRejectionReason = {
  /** The voucher is limited to one use per customer, so a customer email must be set on the checkout before it can be applied. */
  CustomerEmailRequired: 'CUSTOMER_EMAIL_REQUIRED',
  /** The voucher applies to shipping, but no delivery method is selected yet. Selecting one may make the voucher applicable. */
  DeliveryMethodNotSet: 'DELIVERY_METHOD_NOT_SET',
  /** The voucher's end date, or the gift card's expiry date, is in the past. */
  Expired: 'EXPIRED',
  /** The order contains fewer items than the voucher's minimum. Populates the `minCheckoutItemsQuantity` field. */
  MinQuantityNotReached: 'MIN_QUANTITY_NOT_REACHED',
  /** The order value is below the voucher's minimum. Populates the `minSpent` field. */
  MinSpentNotReached: 'MIN_SPENT_NOT_REACHED',
  /** The promo code exists but cannot be used here. Reported when the reason is specific to the code's configuration rather than to something the customer can change: a voucher limited to staff, to other countries, to another channel or to one use per customer, or a gift card restricted to another customer. */
  NotApplicable: 'NOT_APPLICABLE',
  /** No promo code matches the given code. Also reported in place of a reason that may not be disclosed, so this value does not prove that no voucher or gift card exists with that code. */
  NotFound: 'NOT_FOUND',
  /** The voucher applies to specific products, collections or categories, and none of the ordered lines match. */
  NoEligibleProducts: 'NO_ELIGIBLE_PRODUCTS',
  /** The promo code was applicable when it was added to the checkout, but is no longer available. */
  NoLongerAvailable: 'NO_LONGER_AVAILABLE',
  /** The voucher applies to shipping, but nothing in the order requires shipping. */
  ShippingNotRequired: 'SHIPPING_NOT_REQUIRED',
  /** The voucher's total usage limit, summed over all of its codes, is exhausted, or a single-use code was already redeemed. */
  UsageLimitReached: 'USAGE_LIMIT_REACHED'
} as const;

export type PromoCodeRejectionReason = typeof PromoCodeRejectionReason[keyof typeof PromoCodeRejectionReason];
/**
 * Represents possible actions on payment transaction.
 *
 *     The following actions are possible:
 *     CHARGE - Represents the charge action.
 *     REFUND - Represents a refund action.
 *     CANCEL - Represents a cancel action. Added in Saleor 3.12.
 *
 */
export const TransactionActionEnum = {
  Cancel: 'CANCEL',
  Charge: 'CHARGE',
  Refund: 'REFUND'
} as const;

export type TransactionActionEnum = typeof TransactionActionEnum[keyof typeof TransactionActionEnum];
/**
 * Represents possible event types.
 *
 *     Added in Saleor 3.12.
 *
 *     The following types are possible:
 *     AUTHORIZATION_SUCCESS - represents success authorization.
 *     AUTHORIZATION_FAILURE - represents failure authorization.
 *     AUTHORIZATION_ADJUSTMENT - represents authorization adjustment.
 *     AUTHORIZATION_REQUEST - represents authorization request.
 *     AUTHORIZATION_ACTION_REQUIRED - represents authorization that needs
 *     additional actions from the customer.
 *     CHARGE_ACTION_REQUIRED - represents charge that needs
 *     additional actions from the customer.
 *     CHARGE_SUCCESS - represents success charge.
 *     CHARGE_FAILURE - represents failure charge.
 *     CHARGE_BACK - represents chargeback.
 *     CHARGE_REQUEST - represents charge request.
 *     REFUND_SUCCESS - represents success refund.
 *     REFUND_FAILURE - represents failure refund.
 *     REFUND_REVERSE - represents reverse refund.
 *     REFUND_REQUEST - represents refund request.
 *     CANCEL_SUCCESS - represents success cancel.
 *     CANCEL_FAILURE - represents failure cancel.
 *     CANCEL_REQUEST - represents cancel request.
 *     INFO - represents info event.
 *
 */
export const TransactionEventTypeEnum = {
  AuthorizationActionRequired: 'AUTHORIZATION_ACTION_REQUIRED',
  AuthorizationAdjustment: 'AUTHORIZATION_ADJUSTMENT',
  AuthorizationFailure: 'AUTHORIZATION_FAILURE',
  AuthorizationRequest: 'AUTHORIZATION_REQUEST',
  AuthorizationSuccess: 'AUTHORIZATION_SUCCESS',
  CancelFailure: 'CANCEL_FAILURE',
  CancelRequest: 'CANCEL_REQUEST',
  CancelSuccess: 'CANCEL_SUCCESS',
  ChargeActionRequired: 'CHARGE_ACTION_REQUIRED',
  ChargeBack: 'CHARGE_BACK',
  ChargeFailure: 'CHARGE_FAILURE',
  ChargeRequest: 'CHARGE_REQUEST',
  ChargeSuccess: 'CHARGE_SUCCESS',
  Info: 'INFO',
  RefundFailure: 'REFUND_FAILURE',
  RefundRequest: 'REFUND_REQUEST',
  RefundReverse: 'REFUND_REVERSE',
  RefundSuccess: 'REFUND_SUCCESS'
} as const;

export type TransactionEventTypeEnum = typeof TransactionEventTypeEnum[keyof typeof TransactionEventTypeEnum];
/**
 * Determine the transaction flow strategy.
 *
 *     AUTHORIZATION - the processed transaction should be only authorized
 *     CHARGE - the processed transaction should be charged.
 *
 */
export const TransactionFlowStrategyEnum = {
  Authorization: 'AUTHORIZATION',
  Charge: 'CHARGE'
} as const;

export type TransactionFlowStrategyEnum = typeof TransactionFlowStrategyEnum[keyof typeof TransactionFlowStrategyEnum];
export const TransactionInitializeErrorCode = {
  CheckoutCompletionInProgress: 'CHECKOUT_COMPLETION_IN_PROGRESS',
  GraphqlError: 'GRAPHQL_ERROR',
  Invalid: 'INVALID',
  NotFound: 'NOT_FOUND',
  Unique: 'UNIQUE'
} as const;

export type TransactionInitializeErrorCode = typeof TransactionInitializeErrorCode[keyof typeof TransactionInitializeErrorCode];
export const TransactionProcessErrorCode = {
  CheckoutCompletionInProgress: 'CHECKOUT_COMPLETION_IN_PROGRESS',
  GraphqlError: 'GRAPHQL_ERROR',
  Invalid: 'INVALID',
  MissingPaymentApp: 'MISSING_PAYMENT_APP',
  MissingPaymentAppRelation: 'MISSING_PAYMENT_APP_RELATION',
  NotFound: 'NOT_FOUND',
  TransactionAlreadyProcessed: 'TRANSACTION_ALREADY_PROCESSED'
} as const;

export type TransactionProcessErrorCode = typeof TransactionProcessErrorCode[keyof typeof TransactionProcessErrorCode];
export type CheckoutErrorFragmentFragment = { message: string | null, field: string | null, code: CheckoutErrorCode };

export type CheckoutPromoErrorFragmentFragment = { message: string | null, field: string | null, code: CheckoutErrorCode, promoCodeDetails: { reason: PromoCodeRejectionReason, minCheckoutItemsQuantity: number | null, minSpent: { amount: number, currency: string } | null } | null };

export type GiftCardFragmentFragment = { displayCode: string, id: string, currentBalance: { currency: string, amount: number } };

export type ValidationRulesFragmentFragment = { addressFormat: string, allowedFields: Array<string>, requiredFields: Array<string>, countryAreaType: string, postalCodeType: string, postalCodeExamples: Array<string>, cityType: string, countryAreaChoices: Array<{ raw: string | null, verbose: string | null }> };

export type PaymentGatewayFragmentFragment = { id: string, name: string, currencies: Array<string>, config: Array<{ field: string, value: string | null }> };

export type CheckoutFragmentFragment = { id: string, email: string | null, languageCode: LanguageCodeEnum, voucherCode: string | null, discountName: string | null, translatedDiscountName: string | null, authorizeStatus: CheckoutAuthorizeStatusEnum, chargeStatus: CheckoutChargeStatusEnum, isShippingRequired: boolean, discount: { currency: string, amount: number } | null, giftCards: Array<{ displayCode: string, id: string, currentBalance: { currency: string, amount: number } }>, channel: { id: string, slug: string, countries: Array<{ code: string }> | null }, shippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, metadata: Array<{ key: string, value: string }>, country: { country: string, code: string } } | null, billingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, availablePaymentGateways: Array<{ id: string, name: string, currencies: Array<string>, config: Array<{ field: string, value: string | null }> }>, delivery: { id: string, shippingMethod: { id: string, name: string } | null } | null, problems: Array<
    | { __typename: 'CheckoutLineProblemInsufficientStock' }
    | { __typename: 'CheckoutLineProblemVariantNotAvailable' }
    | { __typename: 'CheckoutProblemDeliveryMethodInvalid', delivery: { id: string } }
    | { __typename: 'CheckoutProblemDeliveryMethodStale', delivery: { id: string } }
  > | null, totalPrice: { gross: { currency: string, amount: number }, tax: { currency: string, amount: number } }, shippingPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, subtotalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, lines: Array<{ id: string, quantity: number, metadata: Array<{ key: string, value: string }>, totalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, unitPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, undiscountedUnitPrice: { currency: string, amount: number }, variant: { id: string, name: string, pricing: { price: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } } | null } | null, attributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null, dateTime: string | null, boolean: boolean | null, translation: { name: string } | null }> }>, translation: { name: string } | null, product: { name: string, translation: { id: string, name: string | null, language: { code: LanguageCodeEnum } } | null, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null }, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null } }> };

export type CheckoutLineFragmentFragment = { id: string, quantity: number, metadata: Array<{ key: string, value: string }>, totalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, unitPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, undiscountedUnitPrice: { currency: string, amount: number }, variant: { id: string, name: string, pricing: { price: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } } | null } | null, attributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null, dateTime: string | null, boolean: boolean | null, translation: { name: string } | null }> }>, translation: { name: string } | null, product: { name: string, translation: { id: string, name: string | null, language: { code: LanguageCodeEnum } } | null, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null }, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null } };

export type AddressFragmentFragment = { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } };

export type DeliveryOptionFragmentFragment = { id: string, shippingMethod: { id: string, name: string, maximumDeliveryDays: number | null, minimumDeliveryDays: number | null, price: { currency: string, amount: number } } | null };

export type CheckoutQueryVariables = Exact<{
  id: string | number;
  languageCode: LanguageCodeEnum;
}>;


export type CheckoutQuery = { checkout: { id: string, email: string | null, languageCode: LanguageCodeEnum, voucherCode: string | null, discountName: string | null, translatedDiscountName: string | null, authorizeStatus: CheckoutAuthorizeStatusEnum, chargeStatus: CheckoutChargeStatusEnum, isShippingRequired: boolean, discount: { currency: string, amount: number } | null, giftCards: Array<{ displayCode: string, id: string, currentBalance: { currency: string, amount: number } }>, channel: { id: string, slug: string, countries: Array<{ code: string }> | null }, shippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, metadata: Array<{ key: string, value: string }>, country: { country: string, code: string } } | null, billingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, availablePaymentGateways: Array<{ id: string, name: string, currencies: Array<string>, config: Array<{ field: string, value: string | null }> }>, delivery: { id: string, shippingMethod: { id: string, name: string } | null } | null, problems: Array<
      | { __typename: 'CheckoutLineProblemInsufficientStock' }
      | { __typename: 'CheckoutLineProblemVariantNotAvailable' }
      | { __typename: 'CheckoutProblemDeliveryMethodInvalid', delivery: { id: string } }
      | { __typename: 'CheckoutProblemDeliveryMethodStale', delivery: { id: string } }
    > | null, totalPrice: { gross: { currency: string, amount: number }, tax: { currency: string, amount: number } }, shippingPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, subtotalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, lines: Array<{ id: string, quantity: number, metadata: Array<{ key: string, value: string }>, totalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, unitPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, undiscountedUnitPrice: { currency: string, amount: number }, variant: { id: string, name: string, pricing: { price: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } } | null } | null, attributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null, dateTime: string | null, boolean: boolean | null, translation: { name: string } | null }> }>, translation: { name: string } | null, product: { name: string, translation: { id: string, name: string | null, language: { code: LanguageCodeEnum } } | null, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null }, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null } }> } | null };

export type ChannelQueryVariables = Exact<{
  slug: string;
}>;


export type ChannelQuery = { channel: { countries: Array<{ code: string }> | null } | null };

export type ShopCountriesQueryVariables = Exact<{ [key: string]: never; }>;


export type ShopCountriesQuery = { shop: { countries: Array<{ code: string }> } };

export type CheckoutLinesUpdateMutationVariables = Exact<{
  checkoutId: string | number;
  lines: Array<CheckoutLineUpdateInput> | CheckoutLineUpdateInput;
  languageCode: LanguageCodeEnum;
}>;


export type CheckoutLinesUpdateMutation = { checkoutLinesUpdate: { errors: Array<{ message: string | null, field: string | null, code: CheckoutErrorCode }>, checkout: { id: string, email: string | null, languageCode: LanguageCodeEnum, voucherCode: string | null, discountName: string | null, translatedDiscountName: string | null, authorizeStatus: CheckoutAuthorizeStatusEnum, chargeStatus: CheckoutChargeStatusEnum, isShippingRequired: boolean, discount: { currency: string, amount: number } | null, giftCards: Array<{ displayCode: string, id: string, currentBalance: { currency: string, amount: number } }>, channel: { id: string, slug: string, countries: Array<{ code: string }> | null }, shippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, metadata: Array<{ key: string, value: string }>, country: { country: string, code: string } } | null, billingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, availablePaymentGateways: Array<{ id: string, name: string, currencies: Array<string>, config: Array<{ field: string, value: string | null }> }>, delivery: { id: string, shippingMethod: { id: string, name: string } | null } | null, problems: Array<
        | { __typename: 'CheckoutLineProblemInsufficientStock' }
        | { __typename: 'CheckoutLineProblemVariantNotAvailable' }
        | { __typename: 'CheckoutProblemDeliveryMethodInvalid', delivery: { id: string } }
        | { __typename: 'CheckoutProblemDeliveryMethodStale', delivery: { id: string } }
      > | null, totalPrice: { gross: { currency: string, amount: number }, tax: { currency: string, amount: number } }, shippingPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, subtotalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, lines: Array<{ id: string, quantity: number, metadata: Array<{ key: string, value: string }>, totalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, unitPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, undiscountedUnitPrice: { currency: string, amount: number }, variant: { id: string, name: string, pricing: { price: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } } | null } | null, attributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null, dateTime: string | null, boolean: boolean | null, translation: { name: string } | null }> }>, translation: { name: string } | null, product: { name: string, translation: { id: string, name: string | null, language: { code: LanguageCodeEnum } } | null, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null }, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null } }> } | null } | null };

export type CheckoutLineDeleteMutationVariables = Exact<{
  checkoutId: string | number;
  lineId?: string | number | null | undefined;
  languageCode: LanguageCodeEnum;
}>;


export type CheckoutLineDeleteMutation = { checkoutLineDelete: { errors: Array<{ message: string | null, field: string | null, code: CheckoutErrorCode }>, checkout: { id: string, email: string | null, languageCode: LanguageCodeEnum, voucherCode: string | null, discountName: string | null, translatedDiscountName: string | null, authorizeStatus: CheckoutAuthorizeStatusEnum, chargeStatus: CheckoutChargeStatusEnum, isShippingRequired: boolean, discount: { currency: string, amount: number } | null, giftCards: Array<{ displayCode: string, id: string, currentBalance: { currency: string, amount: number } }>, channel: { id: string, slug: string, countries: Array<{ code: string }> | null }, shippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, metadata: Array<{ key: string, value: string }>, country: { country: string, code: string } } | null, billingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, availablePaymentGateways: Array<{ id: string, name: string, currencies: Array<string>, config: Array<{ field: string, value: string | null }> }>, delivery: { id: string, shippingMethod: { id: string, name: string } | null } | null, problems: Array<
        | { __typename: 'CheckoutLineProblemInsufficientStock' }
        | { __typename: 'CheckoutLineProblemVariantNotAvailable' }
        | { __typename: 'CheckoutProblemDeliveryMethodInvalid', delivery: { id: string } }
        | { __typename: 'CheckoutProblemDeliveryMethodStale', delivery: { id: string } }
      > | null, totalPrice: { gross: { currency: string, amount: number }, tax: { currency: string, amount: number } }, shippingPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, subtotalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, lines: Array<{ id: string, quantity: number, metadata: Array<{ key: string, value: string }>, totalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, unitPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, undiscountedUnitPrice: { currency: string, amount: number }, variant: { id: string, name: string, pricing: { price: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } } | null } | null, attributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null, dateTime: string | null, boolean: boolean | null, translation: { name: string } | null }> }>, translation: { name: string } | null, product: { name: string, translation: { id: string, name: string | null, language: { code: LanguageCodeEnum } } | null, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null }, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null } }> } | null } | null };

export type CheckoutEmailUpdateMutationVariables = Exact<{
  email: string;
  checkoutId: string | number;
  languageCode: LanguageCodeEnum;
}>;


export type CheckoutEmailUpdateMutation = { checkoutEmailUpdate: { errors: Array<{ message: string | null, field: string | null, code: CheckoutErrorCode }>, checkout: { id: string, email: string | null, languageCode: LanguageCodeEnum, voucherCode: string | null, discountName: string | null, translatedDiscountName: string | null, authorizeStatus: CheckoutAuthorizeStatusEnum, chargeStatus: CheckoutChargeStatusEnum, isShippingRequired: boolean, discount: { currency: string, amount: number } | null, giftCards: Array<{ displayCode: string, id: string, currentBalance: { currency: string, amount: number } }>, channel: { id: string, slug: string, countries: Array<{ code: string }> | null }, shippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, metadata: Array<{ key: string, value: string }>, country: { country: string, code: string } } | null, billingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, availablePaymentGateways: Array<{ id: string, name: string, currencies: Array<string>, config: Array<{ field: string, value: string | null }> }>, delivery: { id: string, shippingMethod: { id: string, name: string } | null } | null, problems: Array<
        | { __typename: 'CheckoutLineProblemInsufficientStock' }
        | { __typename: 'CheckoutLineProblemVariantNotAvailable' }
        | { __typename: 'CheckoutProblemDeliveryMethodInvalid', delivery: { id: string } }
        | { __typename: 'CheckoutProblemDeliveryMethodStale', delivery: { id: string } }
      > | null, totalPrice: { gross: { currency: string, amount: number }, tax: { currency: string, amount: number } }, shippingPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, subtotalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, lines: Array<{ id: string, quantity: number, metadata: Array<{ key: string, value: string }>, totalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, unitPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, undiscountedUnitPrice: { currency: string, amount: number }, variant: { id: string, name: string, pricing: { price: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } } | null } | null, attributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null, dateTime: string | null, boolean: boolean | null, translation: { name: string } | null }> }>, translation: { name: string } | null, product: { name: string, translation: { id: string, name: string | null, language: { code: LanguageCodeEnum } } | null, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null }, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null } }> } | null } | null };

export type CheckoutEmailUpdateEarlyMutationVariables = Exact<{
  email: string;
  checkoutId: string | number;
}>;


export type CheckoutEmailUpdateEarlyMutation = { checkoutEmailUpdate: { errors: Array<{ message: string | null, field: string | null, code: CheckoutErrorCode }> } | null };

export type CheckoutCustomerAttachMutationVariables = Exact<{
  checkoutId: string | number;
  languageCode: LanguageCodeEnum;
}>;


export type CheckoutCustomerAttachMutation = { checkoutCustomerAttach: { errors: Array<{ message: string | null, field: string | null, code: CheckoutErrorCode }>, checkout: { id: string, email: string | null, languageCode: LanguageCodeEnum, voucherCode: string | null, discountName: string | null, translatedDiscountName: string | null, authorizeStatus: CheckoutAuthorizeStatusEnum, chargeStatus: CheckoutChargeStatusEnum, isShippingRequired: boolean, discount: { currency: string, amount: number } | null, giftCards: Array<{ displayCode: string, id: string, currentBalance: { currency: string, amount: number } }>, channel: { id: string, slug: string, countries: Array<{ code: string }> | null }, shippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, metadata: Array<{ key: string, value: string }>, country: { country: string, code: string } } | null, billingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, availablePaymentGateways: Array<{ id: string, name: string, currencies: Array<string>, config: Array<{ field: string, value: string | null }> }>, delivery: { id: string, shippingMethod: { id: string, name: string } | null } | null, problems: Array<
        | { __typename: 'CheckoutLineProblemInsufficientStock' }
        | { __typename: 'CheckoutLineProblemVariantNotAvailable' }
        | { __typename: 'CheckoutProblemDeliveryMethodInvalid', delivery: { id: string } }
        | { __typename: 'CheckoutProblemDeliveryMethodStale', delivery: { id: string } }
      > | null, totalPrice: { gross: { currency: string, amount: number }, tax: { currency: string, amount: number } }, shippingPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, subtotalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, lines: Array<{ id: string, quantity: number, metadata: Array<{ key: string, value: string }>, totalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, unitPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, undiscountedUnitPrice: { currency: string, amount: number }, variant: { id: string, name: string, pricing: { price: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } } | null } | null, attributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null, dateTime: string | null, boolean: boolean | null, translation: { name: string } | null }> }>, translation: { name: string } | null, product: { name: string, translation: { id: string, name: string | null, language: { code: LanguageCodeEnum } } | null, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null }, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null } }> } | null } | null };

export type CheckoutShippingAddressUpdateMutationVariables = Exact<{
  checkoutId: string | number;
  shippingAddress: AddressInput;
  validationRules?: CheckoutAddressValidationRules | null | undefined;
  languageCode: LanguageCodeEnum;
}>;


export type CheckoutShippingAddressUpdateMutation = { checkoutShippingAddressUpdate: { errors: Array<{ message: string | null, field: string | null, code: CheckoutErrorCode }>, checkout: { id: string, email: string | null, languageCode: LanguageCodeEnum, voucherCode: string | null, discountName: string | null, translatedDiscountName: string | null, authorizeStatus: CheckoutAuthorizeStatusEnum, chargeStatus: CheckoutChargeStatusEnum, isShippingRequired: boolean, discount: { currency: string, amount: number } | null, giftCards: Array<{ displayCode: string, id: string, currentBalance: { currency: string, amount: number } }>, channel: { id: string, slug: string, countries: Array<{ code: string }> | null }, shippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, metadata: Array<{ key: string, value: string }>, country: { country: string, code: string } } | null, billingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, availablePaymentGateways: Array<{ id: string, name: string, currencies: Array<string>, config: Array<{ field: string, value: string | null }> }>, delivery: { id: string, shippingMethod: { id: string, name: string } | null } | null, problems: Array<
        | { __typename: 'CheckoutLineProblemInsufficientStock' }
        | { __typename: 'CheckoutLineProblemVariantNotAvailable' }
        | { __typename: 'CheckoutProblemDeliveryMethodInvalid', delivery: { id: string } }
        | { __typename: 'CheckoutProblemDeliveryMethodStale', delivery: { id: string } }
      > | null, totalPrice: { gross: { currency: string, amount: number }, tax: { currency: string, amount: number } }, shippingPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, subtotalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, lines: Array<{ id: string, quantity: number, metadata: Array<{ key: string, value: string }>, totalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, unitPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, undiscountedUnitPrice: { currency: string, amount: number }, variant: { id: string, name: string, pricing: { price: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } } | null } | null, attributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null, dateTime: string | null, boolean: boolean | null, translation: { name: string } | null }> }>, translation: { name: string } | null, product: { name: string, translation: { id: string, name: string | null, language: { code: LanguageCodeEnum } } | null, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null }, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null } }> } | null } | null };

export type CheckoutShippingAddressUpdateEarlyMutationVariables = Exact<{
  checkoutId: string | number;
  shippingAddress: AddressInput;
  validationRules?: CheckoutAddressValidationRules | null | undefined;
}>;


export type CheckoutShippingAddressUpdateEarlyMutation = { checkoutShippingAddressUpdate: { errors: Array<{ message: string | null, field: string | null, code: CheckoutErrorCode }> } | null };

export type CheckoutBillingAddressUpdateMutationVariables = Exact<{
  checkoutId: string | number;
  billingAddress: AddressInput;
  validationRules?: CheckoutAddressValidationRules | null | undefined;
  languageCode: LanguageCodeEnum;
}>;


export type CheckoutBillingAddressUpdateMutation = { checkoutBillingAddressUpdate: { errors: Array<{ message: string | null, field: string | null, code: CheckoutErrorCode }>, checkout: { id: string, email: string | null, languageCode: LanguageCodeEnum, voucherCode: string | null, discountName: string | null, translatedDiscountName: string | null, authorizeStatus: CheckoutAuthorizeStatusEnum, chargeStatus: CheckoutChargeStatusEnum, isShippingRequired: boolean, discount: { currency: string, amount: number } | null, giftCards: Array<{ displayCode: string, id: string, currentBalance: { currency: string, amount: number } }>, channel: { id: string, slug: string, countries: Array<{ code: string }> | null }, shippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, metadata: Array<{ key: string, value: string }>, country: { country: string, code: string } } | null, billingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, availablePaymentGateways: Array<{ id: string, name: string, currencies: Array<string>, config: Array<{ field: string, value: string | null }> }>, delivery: { id: string, shippingMethod: { id: string, name: string } | null } | null, problems: Array<
        | { __typename: 'CheckoutLineProblemInsufficientStock' }
        | { __typename: 'CheckoutLineProblemVariantNotAvailable' }
        | { __typename: 'CheckoutProblemDeliveryMethodInvalid', delivery: { id: string } }
        | { __typename: 'CheckoutProblemDeliveryMethodStale', delivery: { id: string } }
      > | null, totalPrice: { gross: { currency: string, amount: number }, tax: { currency: string, amount: number } }, shippingPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, subtotalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, lines: Array<{ id: string, quantity: number, metadata: Array<{ key: string, value: string }>, totalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, unitPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, undiscountedUnitPrice: { currency: string, amount: number }, variant: { id: string, name: string, pricing: { price: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } } | null } | null, attributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null, dateTime: string | null, boolean: boolean | null, translation: { name: string } | null }> }>, translation: { name: string } | null, product: { name: string, translation: { id: string, name: string | null, language: { code: LanguageCodeEnum } } | null, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null }, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null } }> } | null } | null };

export type CheckoutDeliveryMethodUpdateMutationVariables = Exact<{
  checkoutId: string | number;
  deliveryMethodId: string | number;
  languageCode: LanguageCodeEnum;
}>;


export type CheckoutDeliveryMethodUpdateMutation = { checkoutDeliveryMethodUpdate: { errors: Array<{ message: string | null, field: string | null, code: CheckoutErrorCode }>, checkout: { id: string, email: string | null, languageCode: LanguageCodeEnum, voucherCode: string | null, discountName: string | null, translatedDiscountName: string | null, authorizeStatus: CheckoutAuthorizeStatusEnum, chargeStatus: CheckoutChargeStatusEnum, isShippingRequired: boolean, discount: { currency: string, amount: number } | null, giftCards: Array<{ displayCode: string, id: string, currentBalance: { currency: string, amount: number } }>, channel: { id: string, slug: string, countries: Array<{ code: string }> | null }, shippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, metadata: Array<{ key: string, value: string }>, country: { country: string, code: string } } | null, billingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, availablePaymentGateways: Array<{ id: string, name: string, currencies: Array<string>, config: Array<{ field: string, value: string | null }> }>, delivery: { id: string, shippingMethod: { id: string, name: string } | null } | null, problems: Array<
        | { __typename: 'CheckoutLineProblemInsufficientStock' }
        | { __typename: 'CheckoutLineProblemVariantNotAvailable' }
        | { __typename: 'CheckoutProblemDeliveryMethodInvalid', delivery: { id: string } }
        | { __typename: 'CheckoutProblemDeliveryMethodStale', delivery: { id: string } }
      > | null, totalPrice: { gross: { currency: string, amount: number }, tax: { currency: string, amount: number } }, shippingPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, subtotalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, lines: Array<{ id: string, quantity: number, metadata: Array<{ key: string, value: string }>, totalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, unitPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, undiscountedUnitPrice: { currency: string, amount: number }, variant: { id: string, name: string, pricing: { price: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } } | null } | null, attributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null, dateTime: string | null, boolean: boolean | null, translation: { name: string } | null }> }>, translation: { name: string } | null, product: { name: string, translation: { id: string, name: string | null, language: { code: LanguageCodeEnum } } | null, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null }, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null } }> } | null } | null };

export type AddressValidationRulesQueryVariables = Exact<{
  countryCode: CountryCode;
}>;


export type AddressValidationRulesQuery = { addressValidationRules: { addressFormat: string, allowedFields: Array<string>, requiredFields: Array<string>, countryAreaType: string, postalCodeType: string, postalCodeExamples: Array<string>, cityType: string, countryAreaChoices: Array<{ raw: string | null, verbose: string | null }> } | null };

export type BrazilCityChoicesQueryVariables = Exact<{
  countryArea: string;
}>;


export type BrazilCityChoicesQuery = { addressValidationRules: { cityChoices: Array<{ raw: string | null, verbose: string | null }> } | null };

export type CheckoutAddPromoCodeMutationVariables = Exact<{
  checkoutId?: string | number | null | undefined;
  promoCode: string;
  languageCode: LanguageCodeEnum;
}>;


export type CheckoutAddPromoCodeMutation = { checkoutAddPromoCode: { errors: Array<{ message: string | null, field: string | null, code: CheckoutErrorCode, promoCodeDetails: { reason: PromoCodeRejectionReason, minCheckoutItemsQuantity: number | null, minSpent: { amount: number, currency: string } | null } | null }>, checkout: { id: string, email: string | null, languageCode: LanguageCodeEnum, voucherCode: string | null, discountName: string | null, translatedDiscountName: string | null, authorizeStatus: CheckoutAuthorizeStatusEnum, chargeStatus: CheckoutChargeStatusEnum, isShippingRequired: boolean, discount: { currency: string, amount: number } | null, giftCards: Array<{ displayCode: string, id: string, currentBalance: { currency: string, amount: number } }>, channel: { id: string, slug: string, countries: Array<{ code: string }> | null }, shippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, metadata: Array<{ key: string, value: string }>, country: { country: string, code: string } } | null, billingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, availablePaymentGateways: Array<{ id: string, name: string, currencies: Array<string>, config: Array<{ field: string, value: string | null }> }>, delivery: { id: string, shippingMethod: { id: string, name: string } | null } | null, problems: Array<
        | { __typename: 'CheckoutLineProblemInsufficientStock' }
        | { __typename: 'CheckoutLineProblemVariantNotAvailable' }
        | { __typename: 'CheckoutProblemDeliveryMethodInvalid', delivery: { id: string } }
        | { __typename: 'CheckoutProblemDeliveryMethodStale', delivery: { id: string } }
      > | null, totalPrice: { gross: { currency: string, amount: number }, tax: { currency: string, amount: number } }, shippingPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, subtotalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, lines: Array<{ id: string, quantity: number, metadata: Array<{ key: string, value: string }>, totalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, unitPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, undiscountedUnitPrice: { currency: string, amount: number }, variant: { id: string, name: string, pricing: { price: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } } | null } | null, attributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null, dateTime: string | null, boolean: boolean | null, translation: { name: string } | null }> }>, translation: { name: string } | null, product: { name: string, translation: { id: string, name: string | null, language: { code: LanguageCodeEnum } } | null, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null }, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null } }> } | null } | null };

export type CheckoutRemovePromoCodeMutationVariables = Exact<{
  checkoutId?: string | number | null | undefined;
  promoCode?: string | null | undefined;
  promoCodeId?: string | number | null | undefined;
  languageCode: LanguageCodeEnum;
}>;


export type CheckoutRemovePromoCodeMutation = { checkoutRemovePromoCode: { errors: Array<{ message: string | null, field: string | null, code: CheckoutErrorCode }>, checkout: { id: string, email: string | null, languageCode: LanguageCodeEnum, voucherCode: string | null, discountName: string | null, translatedDiscountName: string | null, authorizeStatus: CheckoutAuthorizeStatusEnum, chargeStatus: CheckoutChargeStatusEnum, isShippingRequired: boolean, discount: { currency: string, amount: number } | null, giftCards: Array<{ displayCode: string, id: string, currentBalance: { currency: string, amount: number } }>, channel: { id: string, slug: string, countries: Array<{ code: string }> | null }, shippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, metadata: Array<{ key: string, value: string }>, country: { country: string, code: string } } | null, billingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, availablePaymentGateways: Array<{ id: string, name: string, currencies: Array<string>, config: Array<{ field: string, value: string | null }> }>, delivery: { id: string, shippingMethod: { id: string, name: string } | null } | null, problems: Array<
        | { __typename: 'CheckoutLineProblemInsufficientStock' }
        | { __typename: 'CheckoutLineProblemVariantNotAvailable' }
        | { __typename: 'CheckoutProblemDeliveryMethodInvalid', delivery: { id: string } }
        | { __typename: 'CheckoutProblemDeliveryMethodStale', delivery: { id: string } }
      > | null, totalPrice: { gross: { currency: string, amount: number }, tax: { currency: string, amount: number } }, shippingPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, subtotalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, lines: Array<{ id: string, quantity: number, metadata: Array<{ key: string, value: string }>, totalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, unitPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, undiscountedUnitPrice: { currency: string, amount: number }, variant: { id: string, name: string, pricing: { price: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } } | null } | null, attributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null, dateTime: string | null, boolean: boolean | null, translation: { name: string } | null }> }>, translation: { name: string } | null, product: { name: string, translation: { id: string, name: string | null, language: { code: LanguageCodeEnum } } | null, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null }, media: Array<{ alt: string, type: ProductMediaType, url: string }> | null } }> } | null } | null };

export type CheckoutCompleteMutationVariables = Exact<{
  checkoutId: string | number;
}>;


export type CheckoutCompleteMutation = { checkoutComplete: { errors: Array<{ message: string | null, field: string | null, code: CheckoutErrorCode }>, order: { id: string } | null } | null };

export type CheckoutLanguageCodeUpdateMutationVariables = Exact<{
  checkoutId: string | number;
  languageCode: LanguageCodeEnum;
}>;


export type CheckoutLanguageCodeUpdateMutation = { checkoutLanguageCodeUpdate: { errors: Array<{ message: string | null, field: string | null, code: CheckoutErrorCode }>, checkout: { id: string, languageCode: LanguageCodeEnum } | null } | null };

export type DeliveryOptionsCalculateMutationVariables = Exact<{
  checkoutId: string | number;
}>;


export type DeliveryOptionsCalculateMutation = { deliveryOptionsCalculate: { deliveries: Array<{ id: string, shippingMethod: { id: string, name: string, maximumDeliveryDays: number | null, minimumDeliveryDays: number | null, price: { currency: string, amount: number } } | null }>, errors: Array<{ field: string | null, message: string | null, code: DeliveryOptionsCalculateErrorCode }> } | null };

export type MoneyFragment = { currency: string, amount: number };

export type OrderLineFragmentFragment = { id: string, quantity: number, productName: string, variantName: string, variant: { id: string, name: string, attributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null, dateTime: string | null, boolean: boolean | null, translation: { name: string } | null }> }> } | null, totalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, undiscountedUnitPrice: { gross: { currency: string, amount: number } }, unitPrice: { gross: { currency: string, amount: number } }, thumbnail: { alt: string | null, url: string } | null };

export type ShippingFragmentFragment = { name: string, minimumDeliveryDays: number | null, maximumDeliveryDays: number | null };

export type OrderFragmentFragment = { id: string, number: string, userEmail: string | null, isPaid: boolean, chargeStatus: OrderChargeStatusEnum, authorizeStatus: OrderAuthorizeStatusEnum, channel: { slug: string }, discounts: Array<{ type: OrderDiscountType, name: string | null, amount: { currency: string, amount: number } }>, shippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, billingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, deliveryMethod:
    | { name: string, minimumDeliveryDays: number | null, maximumDeliveryDays: number | null }
    | Record<PropertyKey, never>
   | null, total: { gross: { currency: string, amount: number }, tax: { currency: string, amount: number } }, voucher: { code: string | null } | null, shippingPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, subtotal: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, lines: Array<{ id: string, quantity: number, productName: string, variantName: string, variant: { id: string, name: string, attributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null, dateTime: string | null, boolean: boolean | null, translation: { name: string } | null }> }> } | null, totalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, undiscountedUnitPrice: { gross: { currency: string, amount: number } }, unitPrice: { gross: { currency: string, amount: number } }, thumbnail: { alt: string | null, url: string } | null }>, totalBalance: { currency: string, amount: number }, totalCaptured: { currency: string, amount: number } };

export type OrderQueryVariables = Exact<{
  id: string | number;
  languageCode: LanguageCodeEnum;
}>;


export type OrderQuery = { order: { id: string, number: string, userEmail: string | null, isPaid: boolean, chargeStatus: OrderChargeStatusEnum, authorizeStatus: OrderAuthorizeStatusEnum, channel: { slug: string }, discounts: Array<{ type: OrderDiscountType, name: string | null, amount: { currency: string, amount: number } }>, shippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, billingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, deliveryMethod:
      | { name: string, minimumDeliveryDays: number | null, maximumDeliveryDays: number | null }
      | Record<PropertyKey, never>
     | null, total: { gross: { currency: string, amount: number }, tax: { currency: string, amount: number } }, voucher: { code: string | null } | null, shippingPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, subtotal: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, lines: Array<{ id: string, quantity: number, productName: string, variantName: string, variant: { id: string, name: string, attributes: Array<{ attribute: { slug: string }, values: Array<{ name: string | null, value: string | null, dateTime: string | null, boolean: boolean | null, translation: { name: string } | null }> }> } | null, totalPrice: { gross: { currency: string, amount: number }, net: { currency: string, amount: number } }, undiscountedUnitPrice: { gross: { currency: string, amount: number } }, unitPrice: { gross: { currency: string, amount: number } }, thumbnail: { alt: string | null, url: string } | null }>, totalBalance: { currency: string, amount: number }, totalCaptured: { currency: string, amount: number } } | null };

export type PaymentGatewaysInitializeMutationVariables = Exact<{
  checkoutId: string | number;
  paymentGateways?: Array<PaymentGatewayToInitialize> | PaymentGatewayToInitialize | null | undefined;
}>;


export type PaymentGatewaysInitializeMutation = { paymentGatewayInitialize: { errors: Array<{ field: string | null, message: string | null, code: PaymentGatewayInitializeErrorCode }>, gatewayConfigs: Array<{ id: string, data: any, errors: Array<{ field: string | null, message: string | null, code: PaymentGatewayConfigErrorCode }> | null }> | null } | null };

export type TransactionInitializeMutationVariables = Exact<{
  checkoutId: string | number;
  action?: TransactionFlowStrategyEnum | null | undefined;
  paymentGateway: PaymentGatewayToInitialize;
  amount?: number | null | undefined;
}>;


export type TransactionInitializeMutation = { transactionInitialize: { data: any, transaction: { id: string, actions: Array<TransactionActionEnum> } | null, transactionEvent: { message: string, type: TransactionEventTypeEnum | null } | null, errors: Array<{ field: string | null, code: TransactionInitializeErrorCode, message: string | null }> } | null };

export type TransactionProcessMutationVariables = Exact<{
  id: string | number;
  data?: any;
}>;


export type TransactionProcessMutation = { transactionProcess: { data: any, transaction: { id: string, actions: Array<TransactionActionEnum> } | null, transactionEvent: { message: string, type: TransactionEventTypeEnum | null } | null, errors: Array<{ field: string | null, code: TransactionProcessErrorCode, message: string | null }> } | null };

export type AccountErrorFragmentFragment = { message: string | null, field: string | null, code: AccountErrorCode };

export type UserFragmentFragment = { id: string, email: string, addresses: Array<{ id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, metadata: Array<{ key: string, value: string }>, country: { country: string, code: string } }>, defaultBillingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, defaultShippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null };

export type UserQueryVariables = Exact<{ [key: string]: never; }>;


export type UserQuery = { user: { id: string, email: string, addresses: Array<{ id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, metadata: Array<{ key: string, value: string }>, country: { country: string, code: string } }>, defaultBillingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, defaultShippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null } | null };

export type UserRegisterMutationVariables = Exact<{
  input: AccountRegisterInput;
}>;


export type UserRegisterMutation = { accountRegister: { user: { id: string, email: string } | null, errors: Array<{ message: string | null, field: string | null, code: AccountErrorCode }> } | null };

export type RequestPasswordResetMutationVariables = Exact<{
  email: string;
  channel: string;
  redirectUrl: string;
}>;


export type RequestPasswordResetMutation = { requestPasswordReset: { errors: Array<{ message: string | null, field: string | null, code: AccountErrorCode }> } | null };

export type UserAddressDeleteMutationVariables = Exact<{
  id: string | number;
}>;


export type UserAddressDeleteMutation = { accountAddressDelete: { user: { id: string, email: string, addresses: Array<{ id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, metadata: Array<{ key: string, value: string }>, country: { country: string, code: string } }>, defaultBillingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, defaultShippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null } | null, errors: Array<{ message: string | null, field: string | null, code: AccountErrorCode }>, address: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null } | null };

export type UserAddressUpdateMutationVariables = Exact<{
  id: string | number;
  address: AddressInput;
}>;


export type UserAddressUpdateMutation = { accountAddressUpdate: { user: { id: string, email: string, addresses: Array<{ id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, metadata: Array<{ key: string, value: string }>, country: { country: string, code: string } }>, defaultBillingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, defaultShippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null } | null, errors: Array<{ message: string | null, field: string | null, code: AccountErrorCode }>, address: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null } | null };

export type UserAddressCreateMutationVariables = Exact<{
  address: AddressInput;
  type?: AddressTypeEnum | null | undefined;
}>;


export type UserAddressCreateMutation = { accountAddressCreate: { user: { id: string, email: string, addresses: Array<{ id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, metadata: Array<{ key: string, value: string }>, country: { country: string, code: string } }>, defaultBillingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, defaultShippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null } | null, errors: Array<{ message: string | null, field: string | null, code: AccountErrorCode }>, address: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null } | null };

export type UserSetDefaultAddressMutationVariables = Exact<{
  id: string | number;
  type: AddressTypeEnum;
}>;


export type UserSetDefaultAddressMutation = { accountSetDefaultAddress: { user: { id: string, email: string, addresses: Array<{ id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, metadata: Array<{ key: string, value: string }>, country: { country: string, code: string } }>, defaultBillingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null, defaultShippingAddress: { id: string, city: string, phone: string | null, postalCode: string, companyName: string, cityArea: string, streetAddress1: string, streetAddress2: string, countryArea: string, firstName: string, lastName: string, country: { country: string, code: string } } | null } | null, errors: Array<{ message: string | null, field: string | null, code: AccountErrorCode }> } | null };

export const CheckoutErrorFragmentFragmentDoc = gql`
    fragment CheckoutErrorFragment on CheckoutError {
  message
  field
  code
}
    `;
export const CheckoutPromoErrorFragmentFragmentDoc = gql`
    fragment CheckoutPromoErrorFragment on CheckoutError {
  ...CheckoutErrorFragment
  promoCodeDetails {
    reason
    minSpent {
      amount
      currency
    }
    minCheckoutItemsQuantity
  }
}
    ${CheckoutErrorFragmentFragmentDoc}`;
export const ValidationRulesFragmentFragmentDoc = gql`
    fragment ValidationRulesFragment on AddressValidationData {
  addressFormat
  allowedFields
  requiredFields
  countryAreaType
  postalCodeType
  postalCodeExamples
  cityType
  countryAreaChoices {
    raw
    verbose
  }
}
    `;
export const MoneyFragmentDoc = gql`
    fragment Money on Money {
  currency
  amount
}
    `;
export const GiftCardFragmentFragmentDoc = gql`
    fragment GiftCardFragment on GiftCard {
  displayCode
  id
  currentBalance {
    ...Money
  }
}
    ${MoneyFragmentDoc}`;
export const AddressFragmentFragmentDoc = gql`
    fragment AddressFragment on Address {
  id
  city
  phone
  postalCode
  companyName
  cityArea
  streetAddress1
  streetAddress2
  countryArea
  country {
    country
    code
  }
  firstName
  lastName
}
    `;
export const PaymentGatewayFragmentFragmentDoc = gql`
    fragment PaymentGatewayFragment on PaymentGateway {
  id
  name
  currencies
  config {
    field
    value
  }
}
    `;
export const CheckoutLineFragmentFragmentDoc = gql`
    fragment CheckoutLineFragment on CheckoutLine {
  metadata {
    key
    value
  }
  id
  quantity
  totalPrice {
    gross {
      currency
      amount
    }
    net {
      currency
      amount
    }
  }
  unitPrice {
    gross {
      ...Money
    }
    net {
      ...Money
    }
  }
  undiscountedUnitPrice {
    ...Money
  }
  variant {
    pricing {
      price {
        gross {
          ...Money
        }
        net {
          ...Money
        }
      }
    }
    attributes(variantSelection: ALL) {
      attribute {
        slug
      }
      values {
        name
        value
        dateTime
        boolean
        translation(languageCode: $languageCode) {
          name
        }
      }
    }
    id
    name
    translation(languageCode: $languageCode) {
      name
    }
    product {
      name
      translation(languageCode: $languageCode) {
        language {
          code
        }
        id
        name
      }
      media {
        alt
        type
        url(size: 72)
      }
    }
    media {
      alt
      type
      url(size: 72)
    }
  }
}
    ${MoneyFragmentDoc}`;
export const CheckoutFragmentFragmentDoc = gql`
    fragment CheckoutFragment on Checkout {
  id
  email
  languageCode
  discount {
    ...Money
  }
  voucherCode
  discountName
  translatedDiscountName
  giftCards {
    ...GiftCardFragment
  }
  channel {
    id
    slug
    countries {
      code
    }
  }
  shippingAddress {
    ...AddressFragment
    metadata {
      key
      value
    }
  }
  billingAddress {
    ...AddressFragment
  }
  authorizeStatus
  chargeStatus
  isShippingRequired
  availablePaymentGateways {
    ...PaymentGatewayFragment
  }
  delivery {
    id
    shippingMethod {
      id
      name
    }
  }
  problems {
    __typename
    ... on CheckoutProblemDeliveryMethodStale {
      delivery {
        id
      }
    }
    ... on CheckoutProblemDeliveryMethodInvalid {
      delivery {
        id
      }
    }
  }
  totalPrice {
    gross {
      ...Money
    }
    tax {
      ...Money
    }
  }
  shippingPrice {
    gross {
      ...Money
    }
    net {
      ...Money
    }
  }
  subtotalPrice {
    gross {
      ...Money
    }
    net {
      ...Money
    }
  }
  lines {
    ...CheckoutLineFragment
  }
}
    ${MoneyFragmentDoc}
${GiftCardFragmentFragmentDoc}
${AddressFragmentFragmentDoc}
${PaymentGatewayFragmentFragmentDoc}
${CheckoutLineFragmentFragmentDoc}`;
export const DeliveryOptionFragmentFragmentDoc = gql`
    fragment DeliveryOptionFragment on Delivery {
  id
  shippingMethod {
    id
    name
    price {
      ...Money
    }
    maximumDeliveryDays
    minimumDeliveryDays
  }
}
    ${MoneyFragmentDoc}`;
export const ShippingFragmentFragmentDoc = gql`
    fragment ShippingFragment on ShippingMethod {
  name
  minimumDeliveryDays
  maximumDeliveryDays
}
    `;
export const OrderLineFragmentFragmentDoc = gql`
    fragment OrderLineFragment on OrderLine {
  id
  quantity
  variant {
    id
    name
    attributes(variantSelection: ALL) {
      attribute {
        slug
      }
      values {
        name
        value
        dateTime
        boolean
        translation(languageCode: $languageCode) {
          name
        }
      }
    }
  }
  totalPrice {
    gross {
      ...Money
    }
    net {
      ...Money
    }
  }
  undiscountedUnitPrice {
    gross {
      ...Money
    }
  }
  unitPrice {
    gross {
      ...Money
    }
  }
  productName
  variantName
  thumbnail(size: 128, format: WEBP) {
    alt
    url
  }
}
    ${MoneyFragmentDoc}`;
export const OrderFragmentFragmentDoc = gql`
    fragment OrderFragment on Order {
  id
  number
  userEmail
  isPaid
  channel {
    slug
  }
  discounts {
    type
    name
    amount {
      ...Money
    }
  }
  chargeStatus
  authorizeStatus
  shippingAddress {
    ...AddressFragment
  }
  billingAddress {
    ...AddressFragment
  }
  deliveryMethod {
    ...ShippingFragment
  }
  total {
    gross {
      ...Money
    }
    tax {
      ...Money
    }
  }
  voucher {
    code
  }
  shippingPrice {
    gross {
      ...Money
    }
    net {
      ...Money
    }
  }
  subtotal {
    gross {
      ...Money
    }
    net {
      ...Money
    }
  }
  lines {
    ...OrderLineFragment
  }
  totalBalance {
    ...Money
  }
  totalCaptured {
    ...Money
  }
}
    ${MoneyFragmentDoc}
${AddressFragmentFragmentDoc}
${ShippingFragmentFragmentDoc}
${OrderLineFragmentFragmentDoc}`;
export const AccountErrorFragmentFragmentDoc = gql`
    fragment AccountErrorFragment on AccountError {
  message
  field
  code
}
    `;
export const UserFragmentFragmentDoc = gql`
    fragment UserFragment on User {
  id
  email
  addresses {
    ...AddressFragment
    metadata {
      key
      value
    }
  }
  defaultBillingAddress {
    ...AddressFragment
  }
  defaultShippingAddress {
    ...AddressFragment
  }
}
    ${AddressFragmentFragmentDoc}`;
export const CheckoutDocument = gql`
    query checkout($id: ID!, $languageCode: LanguageCodeEnum!) {
  checkout(id: $id) {
    ...CheckoutFragment
  }
}
    ${CheckoutFragmentFragmentDoc}`;

export function useCheckoutQuery(options: Omit<Urql.UseQueryArgs<CheckoutQueryVariables>, 'query'>) {
  return Urql.useQuery<CheckoutQuery, CheckoutQueryVariables>({ query: CheckoutDocument, ...options });
};
export const ChannelDocument = gql`
    query channel($slug: String!) {
  channel(slug: $slug) {
    countries {
      code
    }
  }
}
    `;

export function useChannelQuery(options: Omit<Urql.UseQueryArgs<ChannelQueryVariables>, 'query'>) {
  return Urql.useQuery<ChannelQuery, ChannelQueryVariables>({ query: ChannelDocument, ...options });
};
export const ShopCountriesDocument = gql`
    query shopCountries {
  shop {
    countries {
      code
    }
  }
}
    `;

export function useShopCountriesQuery(options?: Omit<Urql.UseQueryArgs<ShopCountriesQueryVariables>, 'query'>) {
  return Urql.useQuery<ShopCountriesQuery, ShopCountriesQueryVariables>({ query: ShopCountriesDocument, ...options });
};
export const CheckoutLinesUpdateDocument = gql`
    mutation checkoutLinesUpdate($checkoutId: ID!, $lines: [CheckoutLineUpdateInput!]!, $languageCode: LanguageCodeEnum!) {
  checkoutLinesUpdate(id: $checkoutId, lines: $lines) {
    errors {
      ...CheckoutErrorFragment
    }
    checkout {
      ...CheckoutFragment
    }
  }
}
    ${CheckoutErrorFragmentFragmentDoc}
${CheckoutFragmentFragmentDoc}`;

export function useCheckoutLinesUpdateMutation() {
  return Urql.useMutation<CheckoutLinesUpdateMutation, CheckoutLinesUpdateMutationVariables>(CheckoutLinesUpdateDocument);
};
export const CheckoutLineDeleteDocument = gql`
    mutation checkoutLineDelete($checkoutId: ID!, $lineId: ID, $languageCode: LanguageCodeEnum!) {
  checkoutLineDelete(id: $checkoutId, lineId: $lineId) {
    errors {
      ...CheckoutErrorFragment
    }
    checkout {
      ...CheckoutFragment
    }
  }
}
    ${CheckoutErrorFragmentFragmentDoc}
${CheckoutFragmentFragmentDoc}`;

export function useCheckoutLineDeleteMutation() {
  return Urql.useMutation<CheckoutLineDeleteMutation, CheckoutLineDeleteMutationVariables>(CheckoutLineDeleteDocument);
};
export const CheckoutEmailUpdateDocument = gql`
    mutation checkoutEmailUpdate($email: String!, $checkoutId: ID!, $languageCode: LanguageCodeEnum!) {
  checkoutEmailUpdate(email: $email, id: $checkoutId) {
    errors {
      ...CheckoutErrorFragment
    }
    checkout {
      ...CheckoutFragment
    }
  }
}
    ${CheckoutErrorFragmentFragmentDoc}
${CheckoutFragmentFragmentDoc}`;

export function useCheckoutEmailUpdateMutation() {
  return Urql.useMutation<CheckoutEmailUpdateMutation, CheckoutEmailUpdateMutationVariables>(CheckoutEmailUpdateDocument);
};
export const CheckoutEmailUpdateEarlyDocument = gql`
    mutation checkoutEmailUpdateEarly($email: String!, $checkoutId: ID!) {
  checkoutEmailUpdate(email: $email, id: $checkoutId) {
    errors {
      ...CheckoutErrorFragment
    }
  }
}
    ${CheckoutErrorFragmentFragmentDoc}`;

export function useCheckoutEmailUpdateEarlyMutation() {
  return Urql.useMutation<CheckoutEmailUpdateEarlyMutation, CheckoutEmailUpdateEarlyMutationVariables>(CheckoutEmailUpdateEarlyDocument);
};
export const CheckoutCustomerAttachDocument = gql`
    mutation checkoutCustomerAttach($checkoutId: ID!, $languageCode: LanguageCodeEnum!) {
  checkoutCustomerAttach(id: $checkoutId) {
    errors {
      ...CheckoutErrorFragment
    }
    checkout {
      ...CheckoutFragment
    }
  }
}
    ${CheckoutErrorFragmentFragmentDoc}
${CheckoutFragmentFragmentDoc}`;

export function useCheckoutCustomerAttachMutation() {
  return Urql.useMutation<CheckoutCustomerAttachMutation, CheckoutCustomerAttachMutationVariables>(CheckoutCustomerAttachDocument);
};
export const CheckoutShippingAddressUpdateDocument = gql`
    mutation checkoutShippingAddressUpdate($checkoutId: ID!, $shippingAddress: AddressInput!, $validationRules: CheckoutAddressValidationRules, $languageCode: LanguageCodeEnum!) {
  checkoutShippingAddressUpdate(
    id: $checkoutId
    shippingAddress: $shippingAddress
    validationRules: $validationRules
  ) {
    errors {
      ...CheckoutErrorFragment
    }
    checkout {
      ...CheckoutFragment
    }
  }
}
    ${CheckoutErrorFragmentFragmentDoc}
${CheckoutFragmentFragmentDoc}`;

export function useCheckoutShippingAddressUpdateMutation() {
  return Urql.useMutation<CheckoutShippingAddressUpdateMutation, CheckoutShippingAddressUpdateMutationVariables>(CheckoutShippingAddressUpdateDocument);
};
export const CheckoutShippingAddressUpdateEarlyDocument = gql`
    mutation checkoutShippingAddressUpdateEarly($checkoutId: ID!, $shippingAddress: AddressInput!, $validationRules: CheckoutAddressValidationRules) {
  checkoutShippingAddressUpdate(
    id: $checkoutId
    shippingAddress: $shippingAddress
    validationRules: $validationRules
  ) {
    errors {
      ...CheckoutErrorFragment
    }
  }
}
    ${CheckoutErrorFragmentFragmentDoc}`;

export function useCheckoutShippingAddressUpdateEarlyMutation() {
  return Urql.useMutation<CheckoutShippingAddressUpdateEarlyMutation, CheckoutShippingAddressUpdateEarlyMutationVariables>(CheckoutShippingAddressUpdateEarlyDocument);
};
export const CheckoutBillingAddressUpdateDocument = gql`
    mutation checkoutBillingAddressUpdate($checkoutId: ID!, $billingAddress: AddressInput!, $validationRules: CheckoutAddressValidationRules, $languageCode: LanguageCodeEnum!) {
  checkoutBillingAddressUpdate(
    id: $checkoutId
    billingAddress: $billingAddress
    validationRules: $validationRules
  ) {
    errors {
      ...CheckoutErrorFragment
    }
    checkout {
      ...CheckoutFragment
    }
  }
}
    ${CheckoutErrorFragmentFragmentDoc}
${CheckoutFragmentFragmentDoc}`;

export function useCheckoutBillingAddressUpdateMutation() {
  return Urql.useMutation<CheckoutBillingAddressUpdateMutation, CheckoutBillingAddressUpdateMutationVariables>(CheckoutBillingAddressUpdateDocument);
};
export const CheckoutDeliveryMethodUpdateDocument = gql`
    mutation checkoutDeliveryMethodUpdate($checkoutId: ID!, $deliveryMethodId: ID!, $languageCode: LanguageCodeEnum!) {
  checkoutDeliveryMethodUpdate(
    id: $checkoutId
    deliveryMethodId: $deliveryMethodId
  ) {
    errors {
      ...CheckoutErrorFragment
    }
    checkout {
      ...CheckoutFragment
    }
  }
}
    ${CheckoutErrorFragmentFragmentDoc}
${CheckoutFragmentFragmentDoc}`;

export function useCheckoutDeliveryMethodUpdateMutation() {
  return Urql.useMutation<CheckoutDeliveryMethodUpdateMutation, CheckoutDeliveryMethodUpdateMutationVariables>(CheckoutDeliveryMethodUpdateDocument);
};
export const AddressValidationRulesDocument = gql`
    query addressValidationRules($countryCode: CountryCode!) {
  addressValidationRules(countryCode: $countryCode) {
    ...ValidationRulesFragment
  }
}
    ${ValidationRulesFragmentFragmentDoc}`;

export function useAddressValidationRulesQuery(options: Omit<Urql.UseQueryArgs<AddressValidationRulesQueryVariables>, 'query'>) {
  return Urql.useQuery<AddressValidationRulesQuery, AddressValidationRulesQueryVariables>({ query: AddressValidationRulesDocument, ...options });
};
export const BrazilCityChoicesDocument = gql`
    query brazilCityChoices($countryArea: String!) {
  addressValidationRules(countryCode: BR, countryArea: $countryArea) {
    cityChoices {
      raw
      verbose
    }
  }
}
    `;

export function useBrazilCityChoicesQuery(options: Omit<Urql.UseQueryArgs<BrazilCityChoicesQueryVariables>, 'query'>) {
  return Urql.useQuery<BrazilCityChoicesQuery, BrazilCityChoicesQueryVariables>({ query: BrazilCityChoicesDocument, ...options });
};
export const CheckoutAddPromoCodeDocument = gql`
    mutation checkoutAddPromoCode($checkoutId: ID, $promoCode: String!, $languageCode: LanguageCodeEnum!) {
  checkoutAddPromoCode(checkoutId: $checkoutId, promoCode: $promoCode) {
    errors {
      ...CheckoutPromoErrorFragment
    }
    checkout {
      ...CheckoutFragment
    }
  }
}
    ${CheckoutPromoErrorFragmentFragmentDoc}
${CheckoutFragmentFragmentDoc}`;

export function useCheckoutAddPromoCodeMutation() {
  return Urql.useMutation<CheckoutAddPromoCodeMutation, CheckoutAddPromoCodeMutationVariables>(CheckoutAddPromoCodeDocument);
};
export const CheckoutRemovePromoCodeDocument = gql`
    mutation checkoutRemovePromoCode($checkoutId: ID, $promoCode: String, $promoCodeId: ID, $languageCode: LanguageCodeEnum!) {
  checkoutRemovePromoCode(
    checkoutId: $checkoutId
    promoCode: $promoCode
    promoCodeId: $promoCodeId
  ) {
    errors {
      ...CheckoutErrorFragment
    }
    checkout {
      ...CheckoutFragment
    }
  }
}
    ${CheckoutErrorFragmentFragmentDoc}
${CheckoutFragmentFragmentDoc}`;

export function useCheckoutRemovePromoCodeMutation() {
  return Urql.useMutation<CheckoutRemovePromoCodeMutation, CheckoutRemovePromoCodeMutationVariables>(CheckoutRemovePromoCodeDocument);
};
export const CheckoutCompleteDocument = gql`
    mutation checkoutComplete($checkoutId: ID!) {
  checkoutComplete(id: $checkoutId) {
    errors {
      ...CheckoutErrorFragment
    }
    order {
      id
    }
  }
}
    ${CheckoutErrorFragmentFragmentDoc}`;

export function useCheckoutCompleteMutation() {
  return Urql.useMutation<CheckoutCompleteMutation, CheckoutCompleteMutationVariables>(CheckoutCompleteDocument);
};
export const CheckoutLanguageCodeUpdateDocument = gql`
    mutation checkoutLanguageCodeUpdate($checkoutId: ID!, $languageCode: LanguageCodeEnum!) {
  checkoutLanguageCodeUpdate(checkoutId: $checkoutId, languageCode: $languageCode) {
    errors {
      ...CheckoutErrorFragment
    }
    checkout {
      id
      languageCode
    }
  }
}
    ${CheckoutErrorFragmentFragmentDoc}`;

export function useCheckoutLanguageCodeUpdateMutation() {
  return Urql.useMutation<CheckoutLanguageCodeUpdateMutation, CheckoutLanguageCodeUpdateMutationVariables>(CheckoutLanguageCodeUpdateDocument);
};
export const DeliveryOptionsCalculateDocument = gql`
    mutation deliveryOptionsCalculate($checkoutId: ID!) {
  deliveryOptionsCalculate(id: $checkoutId) {
    deliveries {
      ...DeliveryOptionFragment
    }
    errors {
      field
      message
      code
    }
  }
}
    ${DeliveryOptionFragmentFragmentDoc}`;

export function useDeliveryOptionsCalculateMutation() {
  return Urql.useMutation<DeliveryOptionsCalculateMutation, DeliveryOptionsCalculateMutationVariables>(DeliveryOptionsCalculateDocument);
};
export const OrderDocument = gql`
    query order($id: ID!, $languageCode: LanguageCodeEnum!) {
  order(id: $id) {
    ...OrderFragment
  }
}
    ${OrderFragmentFragmentDoc}`;

export function useOrderQuery(options: Omit<Urql.UseQueryArgs<OrderQueryVariables>, 'query'>) {
  return Urql.useQuery<OrderQuery, OrderQueryVariables>({ query: OrderDocument, ...options });
};
export const PaymentGatewaysInitializeDocument = gql`
    mutation paymentGatewaysInitialize($checkoutId: ID!, $paymentGateways: [PaymentGatewayToInitialize!]) {
  paymentGatewayInitialize(id: $checkoutId, paymentGateways: $paymentGateways) {
    errors {
      field
      message
      code
    }
    gatewayConfigs {
      id
      data
      errors {
        field
        message
        code
      }
    }
  }
}
    `;

export function usePaymentGatewaysInitializeMutation() {
  return Urql.useMutation<PaymentGatewaysInitializeMutation, PaymentGatewaysInitializeMutationVariables>(PaymentGatewaysInitializeDocument);
};
export const TransactionInitializeDocument = gql`
    mutation transactionInitialize($checkoutId: ID!, $action: TransactionFlowStrategyEnum, $paymentGateway: PaymentGatewayToInitialize!, $amount: PositiveDecimal) {
  transactionInitialize(
    id: $checkoutId
    action: $action
    paymentGateway: $paymentGateway
    amount: $amount
  ) {
    transaction {
      id
      actions
    }
    transactionEvent {
      message
      type
    }
    data
    errors {
      field
      code
      message
    }
  }
}
    `;

export function useTransactionInitializeMutation() {
  return Urql.useMutation<TransactionInitializeMutation, TransactionInitializeMutationVariables>(TransactionInitializeDocument);
};
export const TransactionProcessDocument = gql`
    mutation transactionProcess($id: ID!, $data: JSON) {
  transactionProcess(id: $id, data: $data) {
    transaction {
      id
      actions
    }
    transactionEvent {
      message
      type
    }
    data
    errors {
      field
      code
      message
    }
  }
}
    `;

export function useTransactionProcessMutation() {
  return Urql.useMutation<TransactionProcessMutation, TransactionProcessMutationVariables>(TransactionProcessDocument);
};
export const UserDocument = gql`
    query user {
  user: me {
    ...UserFragment
  }
}
    ${UserFragmentFragmentDoc}`;

export function useUserQuery(options?: Omit<Urql.UseQueryArgs<UserQueryVariables>, 'query'>) {
  return Urql.useQuery<UserQuery, UserQueryVariables>({ query: UserDocument, ...options });
};
export const UserRegisterDocument = gql`
    mutation userRegister($input: AccountRegisterInput!) {
  accountRegister(input: $input) {
    user {
      id
      email
    }
    errors {
      message
      field
      code
    }
  }
}
    `;

export function useUserRegisterMutation() {
  return Urql.useMutation<UserRegisterMutation, UserRegisterMutationVariables>(UserRegisterDocument);
};
export const RequestPasswordResetDocument = gql`
    mutation requestPasswordReset($email: String!, $channel: String!, $redirectUrl: String!) {
  requestPasswordReset(
    email: $email
    channel: $channel
    redirectUrl: $redirectUrl
  ) {
    errors {
      message
      field
      code
    }
  }
}
    `;

export function useRequestPasswordResetMutation() {
  return Urql.useMutation<RequestPasswordResetMutation, RequestPasswordResetMutationVariables>(RequestPasswordResetDocument);
};
export const UserAddressDeleteDocument = gql`
    mutation userAddressDelete($id: ID!) {
  accountAddressDelete(id: $id) {
    user {
      ...UserFragment
    }
    errors {
      ...AccountErrorFragment
    }
    address {
      ...AddressFragment
    }
  }
}
    ${UserFragmentFragmentDoc}
${AccountErrorFragmentFragmentDoc}
${AddressFragmentFragmentDoc}`;

export function useUserAddressDeleteMutation() {
  return Urql.useMutation<UserAddressDeleteMutation, UserAddressDeleteMutationVariables>(UserAddressDeleteDocument);
};
export const UserAddressUpdateDocument = gql`
    mutation userAddressUpdate($id: ID!, $address: AddressInput!) {
  accountAddressUpdate(id: $id, input: $address) {
    user {
      ...UserFragment
    }
    errors {
      ...AccountErrorFragment
    }
    address {
      ...AddressFragment
    }
  }
}
    ${UserFragmentFragmentDoc}
${AccountErrorFragmentFragmentDoc}
${AddressFragmentFragmentDoc}`;

export function useUserAddressUpdateMutation() {
  return Urql.useMutation<UserAddressUpdateMutation, UserAddressUpdateMutationVariables>(UserAddressUpdateDocument);
};
export const UserAddressCreateDocument = gql`
    mutation userAddressCreate($address: AddressInput!, $type: AddressTypeEnum) {
  accountAddressCreate(type: $type, input: $address) {
    user {
      ...UserFragment
    }
    errors {
      ...AccountErrorFragment
    }
    address {
      ...AddressFragment
    }
  }
}
    ${UserFragmentFragmentDoc}
${AccountErrorFragmentFragmentDoc}
${AddressFragmentFragmentDoc}`;

export function useUserAddressCreateMutation() {
  return Urql.useMutation<UserAddressCreateMutation, UserAddressCreateMutationVariables>(UserAddressCreateDocument);
};
export const UserSetDefaultAddressDocument = gql`
    mutation userSetDefaultAddress($id: ID!, $type: AddressTypeEnum!) {
  accountSetDefaultAddress(id: $id, type: $type) {
    user {
      ...UserFragment
    }
    errors {
      ...AccountErrorFragment
    }
  }
}
    ${UserFragmentFragmentDoc}
${AccountErrorFragmentFragmentDoc}`;

export function useUserSetDefaultAddressMutation() {
  return Urql.useMutation<UserSetDefaultAddressMutation, UserSetDefaultAddressMutationVariables>(UserSetDefaultAddressDocument);
};